import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLatinKeyboardGroupProps } from '../../hooks/useLatinKeyboardGroupProps';
import { useToast } from '../../contexts/ToastContext';
import {
  fetchItems,
  fetchCategories,
} from '../../store/slices/inventorySlice';
import { fetchOffers } from '../../store/slices/offersSlice';
import {
  createNewOrder,
  switchOrder,
  closeOrder,
  setOrderMode,
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearOrderCart,
  setSelectedCategory,
  setPaymentMethod,
  setOrderDiscountPercentage,
  processTransaction,
  clearError,
  setDeliveryName,
  setDeliveryPhone,
  setDeliveryAddress,
  setDeliveryRegion,
  setDeliveryPersonId,
  closeReceipt,
  adjustCartForInsufficientStock,
} from '../../store/slices/posSlice';
import {
  fetchCurrentSession,
  openSession,
  closeSession,
} from '../../store/slices/sessionSlice';
import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  CreditCard,
  DollarSign,
  Package,
  Smartphone,
  Wallet,
  Eye,
  Printer,
  X,
  RefreshCw,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Copy,
  Check,
  Tags,
} from 'lucide-react';
import Receipt, { printReceipt, printSessionReceipt, printRefundReceipt } from './Receipt';
import apiService from '../../services/api';
import { subscribeToStoreUpdates, subscribeToDeliveryPersonsUpdates, subscribeToOffersUpdates } from '../../services/socket';
import { formatCurrency, formatEGP, roundCurrency, formatQuantity, roundQuantity, getImageUrl } from '../../utils/format';
import { normalizeReceiptNumInput, normalizeRefundCodeInput } from '../../utils/receiptInput';
import { computeOrderTotals, parseStorePromo } from '../../utils/orderPricing';
import { createFuzzySearch } from '../../utils/searchUtils';
import { getTokens } from '../../utils/tokens';
import { StatusPill, MethodPill } from '../ui/AdminPagePrimitives';
import { OrderItemComponentsList } from '../ui/OrderItemComponentsList';
import { OFFERS_CATEGORY_ID, getCartProductDemand, bundlesAvailableForOffer } from '../../utils/offers';

/** Mixed payment: minimum order total (EGP) per secondary method. Card: > 1; InstaPay/Wallet: >= threshold. */
const MIXED_MIN_CASH_AMOUNT = 1;
/** Minimum auto-calculated secondary amount (total − cash) in mixed payment. */
const MIXED_MIN_SECONDARY_AMOUNT = { instapay: 1, wallet: 5 };
const MAX_ORDERS = 5;

function isMixedAvailableForOrderTotal(method, orderTotal) {
  const total = roundCurrency(orderTotal);
  if (method === 'card') return total > 1;
  if (method === 'instapay') return total >= 2;
  if (method === 'wallet') return total >= 6;
  return false;
}

function getMixedMinSecondaryAmount(method) {
  return MIXED_MIN_SECONDARY_AMOUNT[method] ?? 0;
}

/** Largest cash amount allowed so secondary (total − cash) meets minimums. */
function getMixedMaxCashAmount(method, orderTotal) {
  const total = roundCurrency(orderTotal);
  if (method === 'card') {
    return Math.max(MIXED_MIN_CASH_AMOUNT, roundCurrency(total - 0.01));
  }
  const minSecondary = getMixedMinSecondaryAmount(method);
  return Math.max(MIXED_MIN_CASH_AMOUNT, roundCurrency(total - minSecondary));
}

function getMixedSecondaryAmountFromCash(orderTotal, cashAmount) {
  return roundCurrency(orderTotal - (parseFloat(cashAmount) || 0));
}

// Separate component for cart quantity input with inline token styles
const CartQuantityInput = ({ item, tk, onUpdate, onAfterEdit, inputRef }) => {
  const skipBlurCommitRef = React.useRef(false);
  const [localValue, setLocalValue] = useState(
    item.unit_type === 'weight' ? formatQuantity(item.quantity) : String(item.quantity)
  );
  const [isFocused, setIsFocused] = useState(false);

  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(
        item.unit_type === 'weight' ? formatQuantity(item.quantity) : String(item.quantity)
      );
    }
  }, [item.quantity, item.unit_type, isFocused]);

  const handleChange = (e) => {
    let val = e.target.value;
    val = val.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 3) {
      val = parts[0] + '.' + parts[1].slice(0, 3);
    }
    setLocalValue(val);
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      onAfterEdit?.();
      return;
    }
    const val = localValue.trim();
    if (val === '' || val === '.' || val === '0' || val === '0.') {
      setLocalValue(
        item.unit_type === 'weight' ? formatQuantity(item.quantity) : String(item.quantity)
      );
      onAfterEdit?.();
      return;
    }
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed > 0) {
      if (item.unit_type === 'weight') {
        const newValue = Math.round(parsed * 1000) / 1000;
        onUpdate(item.id, newValue);
        setLocalValue(formatQuantity(newValue));
      } else {
        const newValue = Math.floor(parsed);
        if (newValue > 0) {
          onUpdate(item.id, newValue);
          setLocalValue(String(newValue));
        } else {
          setLocalValue(String(item.quantity));
        }
      }
    } else {
      setLocalValue(
        item.unit_type === 'weight' ? formatQuantity(item.quantity) : String(item.quantity)
      );
    }
    onAfterEdit?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      skipBlurCommitRef.current = true;
      setLocalValue(
        item.unit_type === 'weight' ? formatQuantity(item.quantity) : String(item.quantity)
      );
      e.target.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={localValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        width: item.unit_type === 'weight' ? 52 : 44,
        textAlign: 'center',
        background: tk.sunken,
        border: `1px solid ${tk.border}`,
        borderRadius: 4,
        color: tk.text,
        fontSize: 12,
        padding: '2px 4px',
        fontFamily: '"JetBrains Mono", monospace',
        outline: 'none',
      }}
      className="no-spinners"
    />
  );
};

const POS = () => {
  const dispatch = useDispatch();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [noAddressFound, setNoAddressFound] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [cardTransactionNo, setCardTransactionNo] = useState('');
  const [instapayAmount, setInstapayAmount] = useState('');
  const [instapayTransactionNo, setInstapayTransactionNo] = useState('');
  const [walletAmount, setWalletAmount] = useState('');
  const [walletTransactionNo, setWalletTransactionNo] = useState('');
  const [mixedSecondaryMethod, setMixedSecondaryMethod] = useState('card');
  const [electronicType, setElectronicType] = useState('card');
  const [pendingTransaction, setPendingTransaction] = useState(null);
  const [showDiscountApprovalModal, setShowDiscountApprovalModal] = useState(false);
  const [discountApprovalError, setDiscountApprovalError] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [localDiscountInput, setLocalDiscountInput] = useState('');
  const [openingCash, setOpeningCash] = useState('');
  const [showCloseSessionModal, setShowCloseSessionModal] = useState(false);
  const [showSessionOrdersPage, setShowSessionOrdersPage] = useState(false);
  const [showSessionRefundsPage, setShowSessionRefundsPage] = useState(false);
  const [sessionOrders, setSessionOrders] = useState([]);
  const [sessionOrdersLoading, setSessionOrdersLoading] = useState(false);
  const [sessionOrdersError, setSessionOrdersError] = useState('');
  const [sessionOrdersPage, setSessionOrdersPage] = useState(1);
  const [sessionRefunds, setSessionRefunds] = useState([]);
  const [sessionRefundsLoading, setSessionRefundsLoading] = useState(false);
  const [sessionRefundsError, setSessionRefundsError] = useState('');
  const [sessionRefundsPage, setSessionRefundsPage] = useState(1);
  const [viewRefundDetail, setViewRefundDetail] = useState(null);
  const [loadingRefundDetail, setLoadingRefundDetail] = useState(false);
  const [viewOrderDetail, setViewOrderDetail] = useState(null);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);
  const [copiedOrderReceiptNo, setCopiedOrderReceiptNo] = useState(false);
  const [copiedRefundReceiptNo, setCopiedRefundReceiptNo] = useState(false);
  const [sessionRefundsFilters, setSessionRefundsFilters] = useState({
    receipt_num: '',
    order_status: 'all',
    approved_by: 'all',
  });
  const [sessionOrdersFilters, setSessionOrdersFilters] = useState({
    customer_phone: '',
    payment_method: 'all',
    order_type: 'all',
    status: 'all',
    delivery_person_id: 'all',
    start_datetime: '',
    end_datetime: '',
  });
  const [sessionLoading, setSessionLoading] = useState(false);
  const [deliveryRegions, setDeliveryRegions] = useState([]);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [storeName, setStoreName] = useState('');
  const [receiptFooter, setReceiptFooter] = useState('');
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [lowStockThreshold, setLowStockThreshold] = useState(10);
  const [website, setWebsite] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [socialMediaUrls, setSocialMediaUrls] = useState([]);
  const [logoPath, setLogoPath] = useState('');
  const [logoSize, setLogoSize] = useState(100);
  const [includeDeliveryFee, setIncludeDeliveryFee] = useState(true);
  const [requiresRefNum, setRequiresRefNum] = useState(true);
  const [discountRequiresApproval, setDiscountRequiresApproval] = useState(true);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [storePromo, setStorePromo] = useState(null);
  const [discountNotes, setDiscountNotes] = useState('');
  const [discountApproverCode, setDiscountApproverCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const searchInputRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const handleCheckoutRef = useRef(null);
  const cashInputRef = useRef(null);
  const electronicTxInputRef = useRef(null);
  const catRowRef = useRef(null);
  const orderTabsRef = useRef(null);
  const cartScrollRef = useRef(null);
  const lastCartQuantityInputRef = useRef(null);
  const isZeroTotalCheckoutRef = useRef(false);
  const isMixedPaymentAvailableRef = useRef(false);

  const { items, categories } = useSelector((state) => state.inventory);
  const { items: offers } = useSelector((state) => state.offers);
  const pos = useSelector((state) => state.pos);
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const {
    current: session,
    loading: sessionLoadingState,
    error: sessionError,
    ownershipConflict,
  } = useSelector((state) => state.session);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const { containerProps: sessionRefundsReceiptSearchProps } = useLatinKeyboardGroupProps();
  const { containerProps: discountApproverLatinKeyboardProps } = useLatinKeyboardGroupProps();

  const tk = getTokens(mode);

  const activeOrder = pos.orders.find((o) => o.id === pos.activeOrderId) || {};
  const cart = activeOrder.cart || [];
  const paymentMethod = activeOrder.paymentMethod || 'cash';

  const focusLastCartQuantity = useCallback(() => {
    const order = pos.orders.find((o) => o.id === pos.activeOrderId);
    const orderItems = order?.cart || [];
    if (orderItems.length === 0) return;
    lastCartQuantityInputRef.current?.focus();
    lastCartQuantityInputRef.current?.select();
    if (cartScrollRef.current) {
      cartScrollRef.current.scrollTop = cartScrollRef.current.scrollHeight;
    }
  }, [pos.orders, pos.activeOrderId]);
  const delivery = activeOrder.delivery || { phone: '', name: '', address: '' };
  const discountPercentage = activeOrder.discountPercentage ?? 0;
  const selectedCategory = activeOrder.selectedCategory || null;
  const loading = pos.loading;
  const error = pos.error;
  const lastTransaction = pos.lastTransaction;
  const showReceipt = pos.showReceipt;

  useEffect(() => {
    dispatch(fetchItems());
    dispatch(fetchCategories());
    dispatch(fetchOffers(true));
    dispatch(fetchCurrentSession());
  }, [dispatch]);

  useEffect(() => {
    const unsub = subscribeToOffersUpdates(() => dispatch(fetchOffers(true)));
    return () => unsub();
  }, [dispatch]);

  const fetchStoreData = useCallback(() => {
    apiService
      .getStore()
      .then((res) => {
        const data = res.data || {};
        setStoreName(data.name || '');
        setReceiptFooter(data.receipt_footer || '');
        let websiteObj = data.website;
        if (typeof websiteObj === 'string') {
          try {
            const parsed = JSON.parse(websiteObj);
            websiteObj = parsed && typeof parsed === 'object' ? parsed : null;
          } catch {
            websiteObj = { url: websiteObj, include_in_receipt: 'none' };
          }
        }
        setWebsite(websiteObj && typeof websiteObj === 'object' ? websiteObj : null);
        const raw = data.delivery_fee;
        if (raw && typeof raw === 'object') {
          const entries = Object.entries(raw).map(([region, fee]) => ({
            region,
            fee: typeof fee === 'number' ? fee : parseFloat(fee) || 0,
          }));
          setDeliveryRegions(entries);
        } else {
          setDeliveryRegions([]);
        }
        if (data.phone_numbers && typeof data.phone_numbers === 'object') {
          const phones = Object.entries(data.phone_numbers)
            .map(([key, val]) => {
              if (typeof val === 'boolean') return val ? key : null;
              if (typeof val === 'object' && val !== null && val.include_in_receipt !== false)
                return val.number || key;
              return null;
            })
            .filter(Boolean);
          setPhoneNumbers(phones);
        } else {
          setPhoneNumbers([]);
        }
        if (data.social_media_urls && typeof data.social_media_urls === 'object') {
          const socials = Object.entries(data.social_media_urls)
            .filter(([, val]) => {
              if (typeof val === 'object' && val !== null)
                return val.include_in_receipt !== false;
              return true;
            })
            .map(([platform, val]) => ({
              platform,
              url: typeof val === 'object' && val !== null ? val.url || val : val,
            }))
            .filter((s) => s.url);
          setSocialMediaUrls(socials);
        } else {
          setSocialMediaUrls([]);
        }
        setLogoPath(data.logo_path || '');
        const ls = parseInt(data.logo_size, 10);
        setLogoSize(Number.isFinite(ls) && ls >= 40 && ls <= 400 ? ls : 100);
        setTaxPercentage(parseFloat(data.tax) || 0);
        {
          const threshold = parseInt(data.low_stock_threshold, 10);
          setLowStockThreshold(Number.isFinite(threshold) && threshold >= 0 ? threshold : 10);
        }
        setIncludeDeliveryFee(data.include_delivery_fee === true);
        setRequiresRefNum(data.requires_ref_num !== false);
        setDiscountRequiresApproval(data.discount_requires_approval !== false);
        setAllowNegativeStock(data.allow_negative_stock === true);
        const parsedPromo = parseStorePromo(data.promo);
        setStorePromo(
          parsedPromo.promoPct > 0
            ? { name: parsedPromo.promoName, percentage: parsedPromo.promoPct }
            : null
        );
      })
      .catch(() => {
        setDeliveryRegions([]);
        setPhoneNumbers([]);
        setSocialMediaUrls([]);
        setLogoPath('');
        setRequiresRefNum(true);
        setDiscountRequiresApproval(true);
        setAllowNegativeStock(false);
        setLowStockThreshold(10);
        setStorePromo(null);
      });
  }, []);

  useEffect(() => {
    fetchStoreData();
  }, [fetchStoreData]);

  useEffect(() => {
    const unsubscribe = subscribeToStoreUpdates(fetchStoreData);
    return unsubscribe;
  }, [fetchStoreData]);

  const fetchDeliveryPersons = useCallback(() => {
    apiService
      .getDeliveryPersons()
      .then((res) => {
        const list = res?.data || [];
        setDeliveryPersons(list);
        const ids = new Set(list.map((dp) => dp.id));
        const order = pos.orders.find((o) => o.id === pos.activeOrderId);
        if (order?.delivery?.deliveryPersonId && !ids.has(order.delivery.deliveryPersonId)) {
          dispatch(setDeliveryPersonId(null));
        }
      })
      .catch(() => setDeliveryPersons([]));
  }, [dispatch, pos.orders, pos.activeOrderId]);

  useEffect(() => {
    fetchDeliveryPersons();
  }, [fetchDeliveryPersons]);

  useEffect(() => {
    const unsubscribe = subscribeToDeliveryPersonsUpdates(fetchDeliveryPersons);
    return unsubscribe;
  }, [fetchDeliveryPersons]);

  useEffect(() => {
    if (!pos.activeOrderId && pos.orders.length === 0) {
      const timer = setTimeout(() => {
        dispatch(createNewOrder());
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [dispatch, pos.activeOrderId, pos.orders.length]);

  useEffect(() => {
    if (pos.activeOrderId) {
      setBarcodeInput('');
      const timer = setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [pos.activeOrderId]);

  useEffect(() => {
    const PAYMENT_CYCLE = ['cash', 'card', 'instapay', 'wallet', 'mixed'];

    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.code === 'KeyQ') {
        e.preventDefault();
        focusLastCartQuantity();
        return;
      }

      if (e.ctrlKey && e.code === 'KeyD') {
        e.preventDefault();
        const order = pos.orders.find((o) => o.id === pos.activeOrderId);
        const cartItems = order?.cart || [];
        if (cartItems.length > 0) {
          dispatch(removeFromCart(cartItems[cartItems.length - 1].id));
        }
        return;
      }

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          if (pos.orders.length < MAX_ORDERS) {
            dispatch(createNewOrder());
          }
          break;
        case 'F6':
          e.preventDefault();
          handleCheckoutRef.current?.();
          break;
        case 'F2':
          e.preventDefault();
          if (pos.activeOrderId) {
            const others = pos.orders.filter((o) => o.id !== pos.activeOrderId);
            if (others.length > 0) {
              dispatch(switchOrder(others[0].id));
            }
            dispatch(closeOrder(pos.activeOrderId));
          }
          break;
        case 'F4':
          e.preventDefault();
          if (activeOrder.mode === 'delivery') {
            dispatch(setOrderMode('in_store'));
            dispatch(setDeliveryPhone(''));
            dispatch(setDeliveryName(''));
            dispatch(setDeliveryAddress(''));
            dispatch(setDeliveryRegion(''));
            dispatch(setDeliveryPersonId(null));
          } else {
            dispatch(setOrderMode('delivery'));
          }
          break;
        case 'F8':
          e.preventDefault();
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
            barcodeInputRef.current.select();
          }
          break;
        case 'F9':
          e.preventDefault();
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
          break;
        case 'F10':
          e.preventDefault();
          {
            const cycle = isZeroTotalCheckoutRef.current
              ? ['cash']
              : PAYMENT_CYCLE.filter((m) => m !== 'mixed' || isMixedPaymentAvailableRef.current);
            const currentIdx = cycle.indexOf(paymentMethod);
            const next = cycle[(currentIdx + 1) % cycle.length];
            dispatch(setPaymentMethod(next));
            if (['card', 'instapay', 'wallet'].includes(next)) {
              setElectronicType(next);
            }
          }
          break;
        case 'F11':
          e.preventDefault();
          {
            const idx = pos.orders.findIndex((o) => o.id === pos.activeOrderId);
            if (isRTL) {
              if (idx < pos.orders.length - 1) {
                dispatch(switchOrder(pos.orders[idx + 1].id));
              }
            } else {
              if (idx > 0) {
                dispatch(switchOrder(pos.orders[idx - 1].id));
              }
            }
          }
          break;
        case 'F12':
          e.preventDefault();
          {
            const idx = pos.orders.findIndex((o) => o.id === pos.activeOrderId);
            if (isRTL) {
              if (idx > 0) {
                dispatch(switchOrder(pos.orders[idx - 1].id));
              }
            } else {
              if (idx < pos.orders.length - 1) {
                dispatch(switchOrder(pos.orders[idx + 1].id));
              }
            }
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, pos.orders, pos.activeOrderId, activeOrder.mode, paymentMethod, focusLastCartQuantity]);

  useEffect(() => {
    if (!showPaymentModal) return;
    const timer = setTimeout(() => {
      if (paymentMethod === 'cash' || paymentMethod === 'mixed') {
        cashInputRef.current?.focus();
        cashInputRef.current?.select();
      } else if (['card', 'instapay', 'wallet'].includes(paymentMethod)) {
        electronicTxInputRef.current?.focus();
        electronicTxInputRef.current?.select();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [showPaymentModal, paymentMethod]);

  useEffect(() => {
    if (!showPaymentModal) return;
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setShowPaymentModal(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showPaymentModal]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    setLocalDiscountInput(discountPercentage === 0 ? '' : String(discountPercentage));
    setDiscountNotes('');
    setDiscountApproverCode('');
  }, [pos.activeOrderId]);

  useEffect(() => {
    if (discountPercentage <= 0) {
      setDiscountNotes('');
      setDiscountApproverCode('');
    }
  }, [discountPercentage]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (checkoutLoading) {
        e.preventDefault();
        e.returnValue = t('alerts.transactionInProgressConfirm');
        return t('alerts.transactionInProgressConfirm');
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [checkoutLoading, t]);

  useEffect(() => {
    const el = catRowRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      el.scrollLeft += e.deltaY || e.deltaX;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [session?.status, sessionLoadingState]);

  useEffect(() => {
    const el = orderTabsRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      el.scrollLeft += e.deltaY || e.deltaX;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [session?.status]);

  const productStockMap = useMemo(
    () => Object.fromEntries((items || []).map((p) => [p.id, parseFloat(p.stock_quantity) || 0])),
    [items]
  );

  const cartProductDemand = useMemo(() => getCartProductDemand(pos.orders), [pos.orders]);

  const activeOffers = useMemo(
    () => (offers || []).filter((o) => o.is_active !== false),
    [offers]
  );

  useEffect(() => {
    if (selectedCategory === OFFERS_CATEGORY_ID && activeOffers.length === 0) {
      dispatch(setSelectedCategory(null));
    }
  }, [selectedCategory, activeOffers.length, dispatch]);

  const getAvailableStock = (item) => {
    if (item.type === 'offer') {
      return bundlesAvailableForOffer(item, productStockMap, cartProductDemand);
    }
    const totalQuantityInAllOrders = pos.orders.reduce((total, order) => {
      const orderItem = order.cart.find((cartItem) => cartItem.id === item.id);
      return total + (orderItem ? orderItem.quantity : 0);
    }, 0);
    return Math.max(0, (item.stock_quantity || 0) - totalQuantityInAllOrders);
  };

  const baseFiltered = useMemo(() => {
    if (selectedCategory === OFFERS_CATEGORY_ID) {
      return activeOffers;
    }
    if (!selectedCategory) {
      return [...activeOffers, ...(items || [])];
    }
    return (items || []).filter((item) => item.category_id === selectedCategory);
  }, [items, selectedCategory, activeOffers]);
  const searchFn = useMemo(
    () =>
      createFuzzySearch(baseFiltered, {
        keys: selectedCategory === OFFERS_CATEGORY_ID ? ['name', 'sku'] : ['name', 'sku', 'barcode'],
        threshold: 0.4,
        ignoreLocation: true,
      }),
    [baseFiltered, selectedCategory]
  );
  const filteredItems = searchQuery.trim() ? searchFn(searchQuery) : baseFiltered;
  const sortedFilteredItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const aOutOfStock = getAvailableStock(a) <= 0;
      const bOutOfStock = getAvailableStock(b) <= 0;
      if (aOutOfStock === bOutOfStock) return 0;
      return aOutOfStock ? 1 : -1;
    });
  }, [filteredItems, pos.orders]);

  const itemsTotal = cart.reduce((total, item) => total + (item.total || 0), 0);

  const selectedDeliveryRegion = delivery.region || '';
  const selectedRegionFee =
    activeOrder.mode === 'delivery' && selectedDeliveryRegion
      ? deliveryRegions.find((r) => r.region === selectedDeliveryRegion)?.fee || 0
      : 0;

  const promoPct = storePromo?.percentage ?? 0;
  const taxPct = taxPercentage || 0;
  const orderPricing = computeOrderTotals({
    itemsSubtotal: itemsTotal,
    promoPct,
    discountPct: discountPercentage,
    taxPct,
    deliveryFee: selectedRegionFee,
    includeDeliveryInTotal: activeOrder.mode === 'delivery' && includeDeliveryFee,
  });
  const itemsAfterPromo = orderPricing.afterPromo;
  const itemsAfterDiscount = orderPricing.afterDiscount;
  const itemsWithTax = orderPricing.itemsWithTax;
  const cartTotal = orderPricing.total;
  const promoAmount = orderPricing.promoAmount;
  const discountAmount = orderPricing.discountAmount;
  const cartLinesCount = cart.length;
  const discountSaleCanSubmit =
    !discountRequiresApproval || discountApproverCode.trim().length > 0;
  const isZeroTotalCheckout = cart.length > 0 && roundCurrency(cartTotal) === 0;
  const isMixedAvailableForMethod = (method) =>
    cart.length > 0 && isMixedAvailableForOrderTotal(method, cartTotal);
  const isAnyMixedPaymentAvailable =
    isMixedAvailableForMethod('card') ||
    isMixedAvailableForMethod('instapay') ||
    isMixedAvailableForMethod('wallet');
  const isMixedPaymentAvailable = isMixedAvailableForMethod(mixedSecondaryMethod);
  const mixedCashAmount = parseFloat(cashAmount) || 0;
  const mixedMaxCashAllowed =
    paymentMethod === 'mixed' ? getMixedMaxCashAmount(mixedSecondaryMethod, cartTotal) : 0;
  const mixedSecondaryAmount =
    paymentMethod === 'mixed' ? getMixedSecondaryAmountFromCash(cartTotal, cashAmount) : 0;
  const mixedSecondaryBelowMinimum =
    paymentMethod === 'mixed' &&
    getMixedMinSecondaryAmount(mixedSecondaryMethod) > 0 &&
    mixedSecondaryAmount < getMixedMinSecondaryAmount(mixedSecondaryMethod);
  const mixedPaymentInvalid =
    paymentMethod === 'mixed' &&
    (!isMixedPaymentAvailable ||
      !cashAmount ||
      mixedCashAmount < MIXED_MIN_CASH_AMOUNT ||
      mixedCashAmount > mixedMaxCashAllowed ||
      mixedSecondaryBelowMinimum ||
      (mixedSecondaryMethod === 'card' && requiresRefNum && !cardTransactionNo.trim()) ||
      (mixedSecondaryMethod === 'instapay' && requiresRefNum && !instapayTransactionNo.trim()) ||
      (mixedSecondaryMethod === 'wallet' && requiresRefNum && !walletTransactionNo.trim()));
  const cashPaymentInvalid =
    paymentMethod === 'cash' &&
    !isZeroTotalCheckout &&
    cashAmount.trim() !== '' &&
    parseFloat(cashAmount) < cartTotal;

  useEffect(() => {
    isZeroTotalCheckoutRef.current = isZeroTotalCheckout;
    isMixedPaymentAvailableRef.current = isAnyMixedPaymentAvailable;
    if (isZeroTotalCheckout && paymentMethod !== 'cash') {
      dispatch(setPaymentMethod('cash'));
    } else if (paymentMethod === 'mixed' && !isAnyMixedPaymentAvailable) {
      dispatch(setPaymentMethod('cash'));
    } else if (paymentMethod === 'mixed' && !isMixedPaymentAvailable) {
      const fallback = ['card', 'instapay', 'wallet'].find((m) => isMixedAvailableForMethod(m));
      if (fallback) {
        setMixedSecondaryMethod(fallback);
        setCardTransactionNo('');
        setInstapayTransactionNo('');
        setWalletTransactionNo('');
      } else {
        dispatch(setPaymentMethod('cash'));
      }
    }
  }, [
    isZeroTotalCheckout,
    isAnyMixedPaymentAvailable,
    isMixedPaymentAvailable,
    paymentMethod,
    mixedSecondaryMethod,
    dispatch,
    cart.length,
    cartTotal,
  ]);

  const handleAddToCart = (item) => {
    const payload = item.type === 'offer' ? item : { ...item, type: 'product' };
    const availableStock = getAvailableStock(payload);
    if (availableStock <= 0) {
      if (allowNegativeStock) {
        showToast(t('alerts.sellingWithoutStock').replace('{name}', item.name), "warning");
        dispatch(addToCart(payload));
        return;
      }
      const cartItem = cart.find((cartItem) => cartItem.id === item.id);
      const inCart = cartItem ? cartItem.quantity : 0;
      showToast(
        t('alerts.notEnoughStockForItem')
          .replace('{name}', item.name)
          .replace('{total}', item.type === 'offer' ? String(availableStock) : (item.stock_quantity ?? 0))
          .replace('{inCart}', inCart)
          .replace('{available}', availableStock)
      , "warning");
      return;
    }
    dispatch(addToCart(payload));
  };

  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(itemId);
      return;
    }
    const cartItem = cart.find((cartItem) => cartItem.id === itemId);
    if (!cartItem) return;

    if (cartItem.type === 'offer') {
      const demand = getCartProductDemand(pos.orders, { excludeItemId: itemId });
      const available = bundlesAvailableForOffer(cartItem, productStockMap, demand);
      if (newQuantity > available) {
        if (allowNegativeStock) {
          showToast(t('alerts.sellingWithoutStock').replace('{name}', cartItem.name), "warning");
          dispatch(updateCartItemQuantity({ itemId, quantity: newQuantity }));
          return;
        }
        showToast(
          t('alerts.notEnoughStockAvailable')
            .replace('{available}', available)
            .replace('{unit}', t('pos.units'))
        , "warning");
        return;
      }
      dispatch(updateCartItemQuantity({ itemId, quantity: newQuantity }));
      return;
    }

    const item = items.find((item) => item.id === itemId);
    if (!item) return;
    if (newQuantity > (item.stock_quantity || 0)) {
      if (allowNegativeStock) {
        showToast(t('alerts.sellingWithoutStock').replace('{name}', item.name), "warning");
        dispatch(updateCartItemQuantity({ itemId: itemId, quantity: newQuantity }));
        return;
      }
      showToast(
        t('alerts.notEnoughStockAvailable')
          .replace('{available}', item.stock_quantity)
          .replace(
            '{unit}',
            item.unit_type === 'weight' ? t('pos.kg') : t('pos.units')
          )
      , "warning");
      return;
    }
    dispatch(updateCartItemQuantity({ itemId: itemId, quantity: newQuantity }));
  };

  const focusBarcodeInput = useCallback(() => {
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 0);
  }, []);

  const handleRemoveFromCart = (itemId) => {
    dispatch(removeFromCart(itemId));
    focusBarcodeInput();
  };

  const handleDiscountChange = (e) => {
    let val = e.target.value;
    val = val.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      val = parts[0] + '.' + parts[1].slice(0, 2);
    }
    const num = parseFloat(val);
    if (!isNaN(num) && num > 100) {
      val = '100';
    }
    setLocalDiscountInput(val);
    if (val === '') {
      dispatch(setOrderDiscountPercentage(0));
      setDiscountApproverCode('');
    } else if (!isNaN(parseFloat(val))) {
      const nextPct = Math.min(100, Math.max(0, parseFloat(val)));
      if (discountRequiresApproval && nextPct !== discountPercentage) {
        setDiscountApproverCode('');
      }
      dispatch(setOrderDiscountPercentage(nextPct));
    }
  };

  const handleDiscountBlur = () => {
    const num = parseFloat(localDiscountInput);
    if (isNaN(num) || num <= 0) {
      setLocalDiscountInput('');
      dispatch(setOrderDiscountPercentage(0));
      setDiscountApproverCode('');
    } else {
      const clamped = Math.min(100, num);
      setLocalDiscountInput(String(clamped));
      if (discountRequiresApproval && clamped !== discountPercentage) {
        setDiscountApproverCode('');
      }
      dispatch(setOrderDiscountPercentage(clamped));
    }
  };

  const handleAmountInput = (val, setter, maxVal = 9999999.99) => {
    val = val.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      val = parts[0] + '.' + parts[1].slice(0, 2);
    }
    const numVal = parseFloat(val);
    if (!isNaN(numVal) && numVal > maxVal) {
      val = String(maxVal);
    }
    setter(val);
  };

  const parseWeightBarcode = (barcode) => {
    if (!barcode || barcode.length !== 13 || !barcode.startsWith('2')) return null;
    try {
      const sku = barcode.substring(1, 7);
      const weightPortion = barcode.substring(7, 12);
      const weight = parseInt(weightPortion, 10) / 1000;
      return {
        sku,
        weight: weight > 0 ? parseFloat(weight.toFixed(3)) : 0.001,
        isWeightBarcode: true,
      };
    } catch (err) {
      return null;
    }
  };

  const handleBarcodeSubmit = (e) => {
    if (e) e.preventDefault();
    const barcode = barcodeInput.trim();
    if (!barcode) return;

    let item = null;
    let quantityToAdd = 1;

    if (barcode.startsWith('2') && barcode.length === 13) {
      const weightData = parseWeightBarcode(barcode);
      if (!weightData) {
        showToast(t('alerts.invalidWeightBarcode'), "warning");
        setBarcodeInput('');
        return;
      }
      item = items.find((i) => i.sku && i.sku === weightData.sku);
      if (!item) {
        showToast(t('alerts.itemNotFoundForSku').replace('{sku}', weightData.sku), "warning");
        setBarcodeInput('');
        return;
      }
      if (item.unit_type !== 'weight' && item.unit_type !== 'kg') {
        showToast(t('alerts.itemNotWeightConfigured').replace('{name}', item.name), "warning");
        setBarcodeInput('');
        return;
      }
      quantityToAdd = weightData.weight;
    } else {
      item = items.find(
        (i) =>
          (i.barcode && i.barcode === barcode) || (i.sku && i.sku === barcode)
      );
      if (!item) {
        const offerMatch = activeOffers.find((o) => o.sku && o.sku === barcode);
        if (offerMatch) {
          handleAddToCart(offerMatch);
          setBarcodeInput('');
          return;
        }
        showToast(t('alerts.itemNotFoundForBarcode').replace('{barcode}', barcode), "warning");
        setBarcodeInput('');
        return;
      }
      quantityToAdd = 1;
    }

    if ((item.stock_quantity || 0) <= 0 && !allowNegativeStock) {
      showToast(t('alerts.itemOutOfStock').replace('{name}', item.name), "warning");
      setBarcodeInput('');
      return;
    }

    const cartItem = cart.find((ci) => ci.id === item.id);
    const currentQuantity = cartItem ? cartItem.quantity : 0;
    const isWeightItem = item.unit_type === 'weight' || item.unit_type === 'kg';

    if (currentQuantity + quantityToAdd > (item.stock_quantity || 0)) {
      if (allowNegativeStock) {
        showToast(t('alerts.sellingWithoutStock').replace('{name}', item.name), "warning");
      } else {
        showToast(
          t('alerts.notEnoughStockAvailable')
            .replace(
              '{available}',
              ((item.stock_quantity || 0) - currentQuantity).toFixed(isWeightItem ? 3 : 0)
            )
            .replace('{unit}', isWeightItem ? t('pos.kg') : t('pos.units'))
        , "warning");
        setBarcodeInput('');
        return;
      }
    }

    if (cartItem) {
      const newQuantity = isWeightItem
        ? parseFloat((currentQuantity + quantityToAdd).toFixed(3))
        : currentQuantity + quantityToAdd;
      dispatch(updateCartItemQuantity({ itemId: item.id, quantity: newQuantity }));
    } else {
      const cartItemData = { ...item, quantity: quantityToAdd, total: item.price * quantityToAdd };
      dispatch(addToCart(cartItemData));
    }
    setBarcodeInput('');
  };

  const handleCheckout = () => {
    if (!session || session.status !== 'open') {
      showToast(t('alerts.noOpenSession'), "warning");
      return;
    }
    if (activeOrder.mode === 'delivery') {
      if (!delivery.phone || !delivery.phone.trim()) {
        showToast(t('alerts.customerPhoneRequired'), "warning");
        return;
      }
      if (!delivery.name || !delivery.name.trim()) {
        showToast(t('alerts.customerNameRequired'), "warning");
        return;
      }
      if (!delivery.address || !delivery.address.trim()) {
        showToast(t('alerts.customerAddressRequired'), "warning");
        return;
      }
      if (!selectedDeliveryRegion) {
        showToast(t('alerts.pleaseSelectDeliveryRegion'), "warning");
        return;
      }
      if (!delivery.deliveryPersonId) {
        showToast(t('alerts.deliveryPersonRequired'), "warning");
        return;
      }
      if (delivery.phone.trim().length > 15) {
        showToast(t('alerts.customerPhoneMaxLength'), "warning");
        return;
      }
      if (delivery.name.trim().length > 30) {
        showToast(t('alerts.customerNameMaxLength'), "warning");
        return;
      }
      if (delivery.address.trim().length > 50) {
        showToast(t('alerts.posDeliveryAddressMaxLength'), "warning");
        return;
      }
    }
    if (cart.length === 0) {
      showToast(t('alerts.cartIsEmpty'), "warning");
      return;
    }
    setShowPaymentModal(true);
  };

  handleCheckoutRef.current = handleCheckout;

  const handleProcessPayment = async () => {
    const parsedCash = parseFloat(cashAmount) || 0;
    const effectiveCashReceived =
      paymentMethod === 'cash' && cashAmount.trim() === '' ? cartTotal : parsedCash;
    const cash = parsedCash;
    let card = 0;
    let instapay = 0;
    let wallet = 0;
    let cardRef = null;
    let instapayRef = null;
    let walletRef = null;

    if (cashPaymentInvalid) {
      showToast(t('alerts.cashMustBeGreaterOrEqual'), "warning");
      return;
    }
    if (paymentMethod === 'card') {
      if (requiresRefNum && !cardTransactionNo.trim()) {
        showToast(t('alerts.pleaseEnterTransactionNumber'), "warning");
        return;
      }
      if (cardTransactionNo.trim().length > 50) {
        showToast(t('alerts.paymentRefMaxLength'), "warning");
        return;
      }
    }
    if (paymentMethod === 'instapay') {
      if (requiresRefNum && !instapayTransactionNo.trim()) {
        showToast(t('alerts.pleaseEnterTransactionNumber'), "warning");
        return;
      }
      if (instapayTransactionNo.trim().length > 50) {
        showToast(t('alerts.paymentRefMaxLength'), "warning");
        return;
      }
    }
    if (paymentMethod === 'wallet') {
      if (requiresRefNum && !walletTransactionNo.trim()) {
        showToast(t('alerts.pleaseEnterTransactionNumber'), "warning");
        return;
      }
      if (walletTransactionNo.trim().length > 50) {
        showToast(t('alerts.paymentRefMaxLength'), "warning");
        return;
      }
    }
    if (paymentMethod === 'mixed') {
      if (!isMixedPaymentAvailable) {
        showToast(t('alerts.mixedNotAvailableForTotal'), 'warning');
        return;
      }
      if (!cashAmount || cash < MIXED_MIN_CASH_AMOUNT) {
        showToast(t('alerts.mixedMinCashAmount'), 'warning');
        return;
      }
      const remaining = roundCurrency(cartTotal - cash);
      if (remaining <= 0) {
        showToast(t('alerts.cashMustBeLessThanTotal'), "warning");
        return;
      }
      if (mixedSecondaryMethod === 'instapay' && remaining < MIXED_MIN_SECONDARY_AMOUNT.instapay) {
        showToast(t('alerts.mixedMinInstapayAmount'), 'warning');
        return;
      }
      if (mixedSecondaryMethod === 'wallet' && remaining < MIXED_MIN_SECONDARY_AMOUNT.wallet) {
        showToast(t('alerts.mixedMinWalletAmount'), 'warning');
        return;
      }
      if (mixedSecondaryMethod === 'card') {
        card = remaining;
        if (requiresRefNum && !cardTransactionNo.trim()) {
          showToast(t('alerts.pleaseEnterCardTransaction'), "warning");
          return;
        }
        if (cardTransactionNo.trim().length > 50) {
          showToast(t('alerts.paymentRefMaxLength'), "warning");
          return;
        }
        cardRef = cardTransactionNo;
      } else if (mixedSecondaryMethod === 'instapay') {
        instapay = remaining;
        if (requiresRefNum && !instapayTransactionNo.trim()) {
          showToast(t('alerts.pleaseEnterInstapayTransaction'), "warning");
          return;
        }
        if (instapayTransactionNo.trim().length > 50) {
          showToast(t('alerts.paymentRefMaxLength'), "warning");
          return;
        }
        instapayRef = instapayTransactionNo;
      } else if (mixedSecondaryMethod === 'wallet') {
        wallet = remaining;
        if (requiresRefNum && !walletTransactionNo.trim()) {
          showToast(t('alerts.pleaseEnterWalletTransaction'), "warning");
          return;
        }
        if (walletTransactionNo.trim().length > 50) {
          showToast(t('alerts.paymentRefMaxLength'), "warning");
          return;
        }
        walletRef = walletTransactionNo;
      }
    }

    const taxAmount = orderPricing.taxAmount;
    const transactionData = {
      total_amount: cartTotal,
      payment_method: paymentMethod,
      cash_amount:
        paymentMethod === 'cash'
          ? effectiveCashReceived
          : paymentMethod === 'mixed' && cash > 0
            ? cash
            : null,
      card_amount:
        paymentMethod === 'card' ||
        (paymentMethod === 'mixed' && mixedSecondaryMethod === 'card')
          ? paymentMethod === 'card'
            ? cartTotal
            : card
          : null,
      card_transaction_no:
        paymentMethod === 'card' ||
        (paymentMethod === 'mixed' && mixedSecondaryMethod === 'card')
          ? paymentMethod === 'card'
            ? cardTransactionNo
            : cardRef
          : null,
      instapay_amount:
        paymentMethod === 'instapay' ||
        (paymentMethod === 'mixed' && mixedSecondaryMethod === 'instapay')
          ? paymentMethod === 'instapay'
            ? cartTotal
            : instapay
          : null,
      instapay_transaction_no:
        paymentMethod === 'instapay' ||
        (paymentMethod === 'mixed' && mixedSecondaryMethod === 'instapay')
          ? paymentMethod === 'instapay'
            ? instapayTransactionNo
            : instapayRef
          : null,
      wallet_amount:
        paymentMethod === 'wallet' ||
        (paymentMethod === 'mixed' && mixedSecondaryMethod === 'wallet')
          ? paymentMethod === 'wallet'
            ? cartTotal
            : wallet
          : null,
      wallet_transaction_no:
        paymentMethod === 'wallet' ||
        (paymentMethod === 'mixed' && mixedSecondaryMethod === 'wallet')
          ? paymentMethod === 'wallet'
            ? walletTransactionNo
            : walletRef
          : null,
      items: cart.map((item) =>
        item.type === 'offer'
          ? {
              type: 'offer',
              offer_id: item.id,
              item_id: item.id,
              name: item.name,
              item_name: item.name,
              quantity: item.quantity,
              unit_price: item.price,
              total_price: item.total,
            }
          : {
              item_id: item.id,
              product_id: item.id,
              name: item.name,
              item_name: item.name,
              quantity: item.quantity,
              unit_price: item.price,
              total_price: item.total,
            }
      ),
      items_total: itemsTotal,
      discount_percentage: discountPercentage,
      discount_amount: discountAmount,
      tax_percentage: taxPercentage,
      tax_amount: taxAmount,
      delivery_region: activeOrder.mode === 'delivery' ? selectedDeliveryRegion || null : null,
      delivery_fee_amount: activeOrder.mode === 'delivery' ? selectedRegionFee : 0,
      delivery,
      delivery_person_name: delivery.deliveryPersonId
        ? deliveryPersons.find((d) => d.id === delivery.deliveryPersonId)?.name || null
        : null,
      cashier_name: user?.full_name || 'Cashier',
      created_at: new Date().toISOString(),
    };

    setShowPaymentModal(false);

    if (discountPercentage > 0) {
      setPendingTransaction(transactionData);
      setDiscountApprovalError(null);
      setShowDiscountApprovalModal(true);
      return;
    }

    await commitCheckout(transactionData);
  };

  const commitCheckout = async (baseTransactionData, discountFields = null) => {
    const notesTrimmed = (discountFields?.notes ?? '').trim();
    const approverTrimmed = (discountFields?.approverCode ?? '').trim();
    const discountPct = baseTransactionData.discount_percentage || 0;

    const transactionData = {
      ...baseTransactionData,
      discount_approved_by:
        discountPct > 0 && discountRequiresApproval ? approverTrimmed || undefined : undefined,
      discount_notes: discountPct > 0 && notesTrimmed ? notesTrimmed : undefined,
    };

    setCheckoutLoading(true);
    try {
      const result = await dispatch(processTransaction(transactionData)).unwrap();

      if (result.id) {
        const savedItems = (result.items || []).map((oi) => ({
          item_name: oi.product_name,
          name: oi.product_name,
          product_name: oi.product_name,
          quantity: oi.quantity,
          unit_price: parseFloat(oi.unit_price),
          total_price: oi.quantity * parseFloat(oi.unit_price),
        }));
        const printTransaction = {
          ...transactionData,
          receipt_num: result.receipt_num,
          items: savedItems.length > 0 ? savedItems : transactionData.items,
          delivery_region: result.delivery_region ?? transactionData.delivery_region,
          discount_approver_name: result.discount_approver_name || null,
          discount_notes: result.discount_notes || transactionData.discount_notes || null,
          promo_name: result.promo_name || null,
          promo_percentage: parseFloat(result.promo_percentage) || 0,
          change_amount: transactionData.cash_amount
            ? transactionData.cash_amount - transactionData.total_amount
            : 0,
        };
        dispatch(closeReceipt());

        const otherOrders = pos.orders.filter((o) => o.id !== pos.activeOrderId);
        if (otherOrders.length > 0) {
          dispatch(switchOrder(otherOrders[0].id));
          dispatch(closeOrder(pos.activeOrderId));
        } else {
          dispatch(closeOrder(pos.activeOrderId));
        }

        dispatch(clearOrderCart(pos.activeOrderId));

        printReceipt(printTransaction, {
          storeName,
          receiptFooter,
          website,
          phoneNumbers,
          socialMediaUrls,
          logoPath,
          logoSize,
          includeDeliveryFee,
        }).catch((printErr) => {
          console.error('Print failed:', printErr);
        });

        setCashAmount('');
        setCardAmount('');
        setCardTransactionNo('');
        setInstapayAmount('');
        setInstapayTransactionNo('');
        setWalletAmount('');
        setWalletTransactionNo('');
        setMixedSecondaryMethod('card');
        setDiscountNotes('');
        setDiscountApproverCode('');
        setShowDiscountApprovalModal(false);
        setPendingTransaction(null);
        setDiscountApprovalError(null);
      } else {
        throw new Error('Failed to process transaction');
      }
    } catch (err) {
      if (err?.error === 'insufficient_stock') {
        if (err?.offer_id) {
          const offerCart = cart.find((c) => c.type === 'offer' && c.id === err.offer_id);
          const comp = offerCart?.components?.find((c) => c.product_id === err.product_id);
          const maxBundles = comp
            ? Math.floor((parseFloat(err.available) || 0) / (parseFloat(comp.quantity) || 1) * 1000) / 1000
            : 0;
          dispatch(
            adjustCartForInsufficientStock({
              offer_id: err.offer_id,
              available: maxBundles,
            })
          );
        } else if (err?.product_id != null) {
          dispatch(
            adjustCartForInsufficientStock({
              product_id: err.product_id,
              available: err.available,
            })
          );
        }
        setShowDiscountApprovalModal(false);
        setPendingTransaction(null);
        setDiscountApprovalError(null);
        const name = err.product_name || '';
        const avail = parseFloat(err.available) || 0;
        const message =
          avail <= 0
            ? t('alerts.itemRemovedOutOfStock').replace('{name}', name)
            : t('alerts.quantityReducedToAvailable')
                .replace('{name}', name)
                .replace('{available}', err.available ?? '0');
        showToast(message, 'error');
      } else {
        const message =
          err?.error === 'insufficient_stock'
            ? t('alerts.insufficientStockForCheckout')
                .replace('{name}', err.product_name || '')
                .replace('{available}', err.available ?? '0')
            : err?.code === 'INACTIVE_APPROVER'
              ? t('alerts.inactiveApprover')
              : err?.code === 'INVALID_APPROVER'
                ? t('alerts.invalidApprover')
                : err?.code === 'NO_DISCOUNT_PERMISSION'
                  ? t('alerts.noDiscountPermission')
                  : typeof err === 'string'
                    ? err
                    : err?.error || err?.message || t('alerts.unknownError');

        if (
          discountFields !== null &&
          (err?.code === 'INACTIVE_APPROVER' ||
            err?.code === 'INVALID_APPROVER' ||
            err?.code === 'NO_DISCOUNT_PERMISSION' ||
            err?.code === 'MISSING_DISCOUNT_APPROVER')
        ) {
          setDiscountApprovalError(message);
        } else {
          showToast(
            err?.error === 'insufficient_stock'
              ? message
              : t('alerts.transactionFailed').replace('{message}', message),
            'error'
          );
        }
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancelDiscountApproval = () => {
    setShowDiscountApprovalModal(false);
    setPendingTransaction(null);
    setDiscountApprovalError(null);
  };

  useEffect(() => {
    if (!showDiscountApprovalModal) return;
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      handleCancelDiscountApproval();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showDiscountApprovalModal]);

  const handleConfirmDiscountApproval = async () => {
    if (!pendingTransaction) return;

    const notesTrimmed = discountNotes.trim();
    if (notesTrimmed.length > 50) {
      setDiscountApprovalError(t('alerts.discountNotesMaxLength'));
      return;
    }

    if (discountRequiresApproval) {
      const approverTrimmed = normalizeRefundCodeInput(discountApproverCode).trim();
      if (!approverTrimmed) {
        setDiscountApprovalError(t('alerts.pleaseEnterApproverCode'));
        return;
      }
      const refundCodeRegex = /^[A-Za-z0-9]{16}$/;
      if (!refundCodeRegex.test(approverTrimmed)) {
        setDiscountApprovalError(t('alerts.invalidApproverFormat'));
        return;
      }
    }

    setDiscountApprovalError(null);
    await commitCheckout(pendingTransaction, {
      notes: notesTrimmed,
      approverCode: normalizeRefundCodeInput(discountApproverCode).trim(),
    });
  };

  const calculateChange = () => {
    if (paymentMethod === 'cash' && cashAmount) {
      const change = parseFloat(cashAmount) - cartTotal;
      return change >= 0 ? change : 0;
    }
    return 0;
  };

  const getMixedSecondaryAmount = () => {
    if (paymentMethod !== 'mixed' || !cashAmount) return 0;
    const cash = parseFloat(cashAmount) || 0;
    return Math.max(0, cartTotal - cash);
  };

  const handleDeliveryPhoneChange = async (phone) => {
    dispatch(setDeliveryPhone(phone));
    if (phone.length >= 10) {
      try {
        const customers = await apiService.getCustomers(phone);
        const found = customers.data?.find((c) => c.phone === phone);
        if (found) {
          dispatch(setDeliveryName((found.name || '').slice(0, 30)));
          dispatch(setDeliveryAddress((found.address || '').slice(0, 50)));
          setNoAddressFound(false);
        } else {
          dispatch(setDeliveryName(''));
          dispatch(setDeliveryAddress(''));
          setNoAddressFound(true);
        }
      } catch (err) {
        dispatch(setDeliveryName(''));
        dispatch(setDeliveryAddress(''));
        setNoAddressFound(true);
      }
    } else {
      dispatch(setDeliveryName(''));
      dispatch(setDeliveryAddress(''));
      setNoAddressFound(true);
    }
  };

  const handleOpenSession = async (e) => {
    if (e) e.preventDefault();
    const cash = parseFloat(openingCash) || 0;
    if (cash < 0) {
      showToast(t('alerts.openingAmountNegative'), "warning");
      return;
    }
    setSessionLoading(true);
    try {
      await dispatch(openSession({ opening_cash: cash })).unwrap();
      setOpeningCash('');
    } catch (err) {
      const errorMsg =
        typeof err === 'string' ? err : err?.message || t('alerts.failedToOpenSession');
      showToast(errorMsg, "error");
    } finally {
      setSessionLoading(false);
    }
  };

  const handleCloseSession = async () => {
    setSessionLoading(true);
    try {
      const closedSession = await dispatch(closeSession({})).unwrap();
      setShowCloseSessionModal(false);
      await dispatch(fetchCurrentSession());
      if (closedSession) {
        await printSessionReceipt(closedSession, { storeName, logoPath, logoSize });
      }
    } catch (err) {
      const errorMsg =
        typeof err === 'string' ? err : err?.message || t('alerts.failedToCloseSession');
      showToast(errorMsg, "error");
    } finally {
      setSessionLoading(false);
    }
  };

  const handleViewSessionOrder = useCallback(async (order) => {
    setViewOrderDetail(order);
    setLoadingOrderDetail(true);
    try {
      const res = await apiService.getOrder(order.id);
      setViewOrderDetail(res?.data || order);
    } catch {
      // keep basic data already set
    } finally {
      setLoadingOrderDetail(false);
    }
  }, []);

  const handleViewSessionRefund = useCallback(async (refund) => {
    setViewRefundDetail(refund);
    setLoadingRefundDetail(true);
    try {
      const res = await apiService.getRefund(refund.id);
      setViewRefundDetail(res?.data || refund);
    } catch {
      // keep basic data already set
    } finally {
      setLoadingRefundDetail(false);
    }
  }, []);

  const handlePrintSessionOrder = useCallback(
    async (order) => {
      try {
        const res = await apiService.getOrder(order.id);
        const fullOrder = res?.data || order;
        await printReceipt(fullOrder, {
          storeName,
          receiptFooter,
          website,
          phoneNumbers,
          socialMediaUrls,
          logoPath,
          logoSize,
          includeDeliveryFee,
        });
      } catch (err) {
        showToast(err?.body?.error || err?.message || t('alerts.failedToPrintReceipt'), "error");
      }
    },
    [storeName, receiptFooter, website, phoneNumbers, socialMediaUrls, logoPath, logoSize, includeDeliveryFee, t]
  );

  const handlePrintSessionRefund = useCallback(
    async (refund) => {
      try {
        let fullRefund = refund;
        if (!Array.isArray(refund?.items) || refund.items.length === 0) {
          const res = await apiService.getRefund(refund.id);
          fullRefund = res?.data || refund;
        }
        const items = (fullRefund.items || []).map((it) => ({
          product_name: it.product_name || it.item_name || it.name || '',
          refunded_quantity: it.refunded_quantity,
          refunded_amount: it.refunded_amount,
          product_unit_type: it.product_unit_type || it.unit_type,
        }));
        await printRefundReceipt(
          {
            receipt_num: fullRefund.receipt_num || '',
            cashier_name: user?.full_name || fullRefund.cashier_name || t('settings.cashier'),
            refunded_at: fullRefund.created_at || new Date().toISOString(),
            items,
            total: fullRefund.amount,
          },
          { storeName, logoPath, logoSize }
        );
      } catch (err) {
        showToast(err?.body?.error || err?.message || t('alerts.failedToPrintReceipt'), "error");
      }
    },
    [storeName, logoPath, logoSize, user?.full_name, t]
  );

  const loadSessionOrders = useCallback(async () => {
    if (!session?.id) return;
    setSessionOrdersLoading(true);
    setSessionOrdersError('');
    try {
      const params = {
        session_id: session.id,
        limit: 500,
      };
      const phone = sessionOrdersFilters.customer_phone.trim();
      if (phone) params.customer_phone = phone;
      if (sessionOrdersFilters.payment_method !== 'all') params.payment_method = sessionOrdersFilters.payment_method;
      if (sessionOrdersFilters.order_type !== 'all') params.order_type = sessionOrdersFilters.order_type;
      if (sessionOrdersFilters.status !== 'all') params.status = sessionOrdersFilters.status;
      if (sessionOrdersFilters.delivery_person_id !== 'all') params.delivery_person_id = sessionOrdersFilters.delivery_person_id;
      if (sessionOrdersFilters.start_datetime) params.start_date = sessionOrdersFilters.start_datetime.split('T')[0];
      if (sessionOrdersFilters.end_datetime) params.end_date = sessionOrdersFilters.end_datetime.split('T')[0];

      const res = await apiService.getOrders(params);
      setSessionOrders(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setSessionOrdersError(err?.body?.error || err?.message || t('pos.sessionOrdersLoadError'));
      setSessionOrders([]);
    } finally {
      setSessionOrdersLoading(false);
    }
  }, [session?.id, sessionOrdersFilters, t]);

  useEffect(() => {
    const handleOpenSessionOrdersPage = () => {
      setSessionOrdersFilters((prev) => ({
        ...prev,
        start_datetime: '',
        end_datetime: '',
      }));
      setShowSessionOrdersPage(true);
    };
    window.addEventListener('pos:open-session-orders-page', handleOpenSessionOrdersPage);
    return () => window.removeEventListener('pos:open-session-orders-page', handleOpenSessionOrdersPage);
  }, [session]);

  useEffect(() => {
    if (!showSessionOrdersPage) return;
    loadSessionOrders();
  }, [showSessionOrdersPage, loadSessionOrders]);

  useEffect(() => {
    if (!showSessionOrdersPage) return;

    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (viewOrderDetail) {
        setViewOrderDetail(null);
        return;
      }
      setShowSessionOrdersPage(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showSessionOrdersPage, viewOrderDetail]);

  const loadSessionRefunds = useCallback(async () => {
    if (!session?.id) return;
    setSessionRefundsLoading(true);
    setSessionRefundsError('');
    try {
      const params = { session_id: session.id, limit: 500 };
      if (sessionRefundsFilters.receipt_num.trim()) params.receipt_num = sessionRefundsFilters.receipt_num.trim();
      if (sessionRefundsFilters.order_status !== 'all') params.order_status = sessionRefundsFilters.order_status;
      if (sessionRefundsFilters.approved_by !== 'all') params.approved_by = sessionRefundsFilters.approved_by;
      const res = await apiService.getRefunds(params);
      setSessionRefunds(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setSessionRefundsError(err?.body?.error || err?.message || t('pos.sessionRefundsLoadError'));
      setSessionRefunds([]);
    } finally {
      setSessionRefundsLoading(false);
    }
  }, [session?.id, sessionRefundsFilters, t]);

  useEffect(() => {
    const handleOpenSessionRefundsPage = () => {
      setShowSessionRefundsPage(true);
      setShowSessionOrdersPage(false);
    };
    window.addEventListener('pos:open-session-refunds-page', handleOpenSessionRefundsPage);
    return () => window.removeEventListener('pos:open-session-refunds-page', handleOpenSessionRefundsPage);
  }, []);

  useEffect(() => {
    if (!showSessionRefundsPage) return;
    loadSessionRefunds();
  }, [showSessionRefundsPage, loadSessionRefunds]);

  useEffect(() => {
    if (!showSessionRefundsPage) return;

    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (viewRefundDetail) {
        setViewRefundDetail(null);
        return;
      }
      setShowSessionRefundsPage(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showSessionRefundsPage, viewRefundDetail]);

  const sessionRefundApprovers = useMemo(() => {
    const m = new Map();
    sessionRefunds.forEach((r) => {
      if (r.approved_by) m.set(String(r.approved_by), r.approver_name || String(r.approved_by));
    });
    return Array.from(m.entries()).map(([value, label]) => ({ value, label }));
  }, [sessionRefunds]);

  const filteredSessionOrders = useMemo(() => {
    const startTs = sessionOrdersFilters.start_datetime
      ? new Date(sessionOrdersFilters.start_datetime).getTime()
      : null;
    const endTs = sessionOrdersFilters.end_datetime
      ? new Date(sessionOrdersFilters.end_datetime).getTime()
      : null;

    return sessionOrders.filter((order) => {
      const createdTs = new Date(order.created_at).getTime();
      if (startTs && createdTs < startTs) return false;
      if (endTs && createdTs > endTs) return false;
      return true;
    });
  }, [sessionOrders, sessionOrdersFilters.start_datetime, sessionOrdersFilters.end_datetime]);

  const SESSION_LIST_LIMIT = 20;

  useEffect(() => {
    setSessionOrdersPage(1);
  }, [sessionOrdersFilters, filteredSessionOrders.length]);

  const sessionOrdersTotalPages = Math.max(
    1,
    Math.ceil(filteredSessionOrders.length / SESSION_LIST_LIMIT)
  );

  const pagedSessionOrders = useMemo(() => {
    const start = (sessionOrdersPage - 1) * SESSION_LIST_LIMIT;
    return filteredSessionOrders.slice(start, start + SESSION_LIST_LIMIT);
  }, [filteredSessionOrders, sessionOrdersPage]);

  useEffect(() => {
    setSessionRefundsPage(1);
  }, [sessionRefundsFilters, sessionRefunds.length]);

  const sessionRefundsTotalPages = Math.max(
    1,
    Math.ceil(sessionRefunds.length / SESSION_LIST_LIMIT)
  );

  const pagedSessionRefunds = useMemo(() => {
    const start = (sessionRefundsPage - 1) * SESSION_LIST_LIMIT;
    return sessionRefunds.slice(start, start + SESSION_LIST_LIMIT);
  }, [sessionRefunds, sessionRefundsPage]);

  // Returns "YYYY-MM-DDTHH:MM" in the user's local timezone — safe for datetime-local inputs
  const toLocalDT = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // ─── Shared input style ───────────────────────────────────────────────────────
  const inputStyle = {
    background: tk.sunken,
    border: `1px solid ${tk.border}`,
    borderRadius: 7,
    color: tk.text,
    fontSize: 13,
    padding: '6px 10px',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
    height: 34,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    paddingRight: 28,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%238a8a96' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    color: tk.textMuted,
    display: 'block',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  // ─── Loading screen ───────────────────────────────────────────────────────────
  if (sessionLoadingState) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: tk.bg,
        }}
      >
        <div
          style={{
            background: tk.surface,
            border: `1px solid ${tk.border}`,
            borderRadius: 16,
            padding: 32,
            maxWidth: 360,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: `3px solid ${tk.accentSoft}`,
              borderTopColor: tk.accent,
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <p style={{ color: tk.textSecondary, fontSize: 13 }}>
            {t('sessions.loadingSession')}
          </p>
        </div>
      </div>
    );
  }

  // ─── Ownership conflict screen ────────────────────────────────────────────────
  if (ownershipConflict) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: tk.bg,
        }}
      >
        <div
          style={{
            background: tk.surface,
            border: `1px solid ${tk.border}`,
            borderRadius: 16,
            padding: 32,
            maxWidth: 400,
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: tk.dangerSoft,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <X size={22} style={{ color: tk.danger }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: tk.text }}>
                {t('sessions.registerLocked')}
              </div>
              <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 2 }}>
                {t('sessions.registerLockedMessage')}
              </div>
            </div>
          </div>
          <div
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 10,
              background: tk.dangerSoft,
              border: `1px solid ${tk.danger}33`,
            }}
          >
            <p style={{ fontSize: 13, color: tk.text, marginBottom: 8 }}>
              {t('sessions.registerLockedFullMessage').replace(
                '{name}',
                ownershipConflict.details?.cashier_name || ''
              )}
            </p>
            {ownershipConflict.details && (
              <div style={{ fontSize: 11, color: tk.textSecondary, lineHeight: 1.7 }}>
                <div>
                  <strong>{t('sessions.cashier')}:</strong>{' '}
                  {ownershipConflict.details.cashier_name}
                </div>
                <div>
                  <strong>{t('sessions.startedAt')}:</strong>{' '}
                  {new Date(ownershipConflict.details.started_at).toLocaleString()}
                </div>
              </div>
            )}
          </div>
          <p style={{ fontSize: 12, color: tk.textSecondary, marginBottom: 8 }}>
            {t('sessions.registerLockedOptions')}
          </p>
          <ul
            style={{
              fontSize: 12,
              color: tk.textSecondary,
              paddingInlineStart: 20,
              lineHeight: 2,
            }}
          >
            <li>
              {ownershipConflict.details?.cashier_name} {t('sessions.registerLockedOption1')}
            </li>
            <li>{t('sessions.registerLockedOption2')}</li>
          </ul>
        </div>
      </div>
    );
  }

  // ─── Open session screen ──────────────────────────────────────────────────────
  if (!session || session.status !== 'open') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: tk.bg,
        }}
      >
        <div
          style={{
            background: tk.surface,
            border: `1px solid ${tk.border}`,
            borderRadius: 16,
            padding: 32,
            maxWidth: 360,
            width: '100%',
          }}
        >
          {/* Icon box */}
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: tk.accentSoft,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <Wallet size={22} style={{ color: tk.accent }} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: tk.text, marginBottom: 4 }}>
            {t('sessions.openSession')}
          </div>
          <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 24 }}>
            {t('sessions.openSessionSubtitle')}
          </div>

          {sessionError && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 8,
                background: tk.dangerSoft,
                border: `1px solid ${tk.danger}44`,
                color: tk.dangerText,
                fontSize: 13,
              }}
            >
              {typeof sessionError === 'object' ? sessionError.message : sessionError}
            </div>
          )}

          <form onSubmit={handleOpenSession}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>{t('sessions.openingCashLabel')}</label>
              <input
                type="number"
                min="0"
                max="9999999.99"
                step="0.01"
                inputMode="decimal"
                value={openingCash}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '' || raw === '.') {
                    setOpeningCash(raw);
                    return;
                  }
                  if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
                  const n = parseFloat(raw);
                  if (Number.isNaN(n) || n < 0 || n > 9999999.99) return;
                  setOpeningCash(raw);
                }}
                style={{
                  ...inputStyle,
                  height: 44,
                  fontSize: 15,
                  fontFamily: '"JetBrains Mono", monospace',
                }}
                placeholder="0.00"
              />
            </div>
            <button
              type="submit"
              disabled={sessionLoading}
              style={{
                width: '100%',
                height: 44,
                background: sessionLoading ? tk.accentSoft : tk.accent,
                color: sessionLoading ? tk.accentText : '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                cursor: sessionLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'background 0.15s',
              }}
            >
              {sessionLoading ? (
                <>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: `2px solid ${tk.accentText}44`,
                      borderTopColor: tk.accentText,
                      animation: 'spin 0.8s linear infinite',
                      flexShrink: 0,
                    }}
                  />
                  {t('sessions.openingSessionInProgress')}
                </>
              ) : (
                t('pos.openSession')
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showSessionOrdersPage) {
    const now = new Date();
    const nowLocal = toLocalDT(now);
    const startedAt = session?.started_at ? new Date(session.started_at) : now;
    const sessionStartLocal = toLocalDT(startedAt);

    const updateFilter = (key, value) => {
      setSessionOrdersFilters((prev) => {
        let nextStart = key === 'start_datetime' ? value : prev.start_datetime;
        let nextEnd = key === 'end_datetime' ? value : prev.end_datetime;
        if (nextStart && nextStart < sessionStartLocal) nextStart = sessionStartLocal;
        if (nextEnd && nextEnd > nowLocal) nextEnd = nowLocal;
        if (nextStart && nextEnd && nextStart > nextEnd) {
          if (key === 'start_datetime') nextEnd = nextStart;
          else nextStart = nextEnd;
        }
        return { ...prev, [key]: value, start_datetime: nextStart, end_datetime: nextEnd };
      });
    };

    const resetSessionOrdersFilters = () => {
      setSessionOrdersFilters({ customer_phone: '', payment_method: 'all', order_type: 'all', status: 'all', delivery_person_id: 'all', start_datetime: '', end_datetime: '' });
    };

    const getStatusKind = (status) => {
      if (status === 'completed') return 'paid';
      if (status === 'refunded' || status === 'partially_refunded') return 'refunded';
      return 'closed';
    };

    const totalSales = filteredSessionOrders.reduce(
      (s, o) => s + (Number(o.total_amount) || 0),
      0
    );
    const completedCount = filteredSessionOrders.filter(o => o.status === 'completed').length;
    const refundedCount = filteredSessionOrders.filter(o => o.status === 'refunded' || o.status === 'partially_refunded').length;

    const COL = 'minmax(130px, 1.5fr) 145px 48px 110px 110px 100px 68px';

    const iconBtn = {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 28, height: 28, borderRadius: 6,
      background: tk.sunken, border: `1px solid ${tk.border}`,
      color: tk.textSecondary, cursor: 'pointer', transition: 'background 0.12s, color 0.12s',
    };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: tk.bg, overflow: 'auto', fontFamily: 'DM Sans, sans-serif', padding: 14 }} dir={isRTL ? 'rtl' : 'ltr'}>
        <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, maxWidth: 1200, margin: '0 auto', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: `1px solid ${tk.border}`, gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: tk.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShoppingCart size={15} color={tk.accentText} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: tk.text, lineHeight: 1.2 }}>{t('pos.sessionOrders')}</div>
                <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 2 }}>{filteredSessionOrders.length} {language === 'ar' ? 'طلب' : 'orders'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" onClick={resetSessionOrdersFilters}
                style={{ height: 30, padding: '0 10px', borderRadius: 7, border: `1px solid ${tk.border}`, background: 'transparent', color: tk.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }}>
                {t('common.reset')}
              </button>
              <button type="button" onClick={loadSessionOrders} disabled={sessionOrdersLoading}
                style={{ height: 30, padding: '0 10px', borderRadius: 7, border: `1px solid ${tk.border}`, background: tk.surfaceAlt, color: tk.textSecondary, cursor: sessionOrdersLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', opacity: sessionOrdersLoading ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <RefreshCw size={12} />
                {t('common.refresh')}
              </button>
              <button type="button" onClick={() => setShowSessionOrdersPage(false)}
                style={{ height: 30, padding: '0 10px', borderRadius: 7, border: `1px solid ${tk.border}`, background: 'transparent', color: tk.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <X size={13} />
                {t('common.back')}
              </button>
            </div>
          </div>

          {/* Summary stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: `1px solid ${tk.border}` }}>
              {[
                { label: language === 'ar' ? 'إجمالي الطلبات' : 'Total Orders', value: filteredSessionOrders.length, mono: true },
                { label: language === 'ar' ? 'إجمالي المبيعات' : 'Total Sales', value: formatEGP(roundCurrency(totalSales)), mono: true },
                { label: language === 'ar' ? 'مكتملة' : 'Completed', value: completedCount, mono: true, color: tk.successText },
                { label: language === 'ar' ? 'مرتجعة' : 'Refunded', value: refundedCount, mono: true, color: tk.dangerText },
              ].map((s, i) => (
                <div key={i} style={{ padding: '10px 16px', borderInlineEnd: i < 3 ? `1px solid ${tk.border}` : 'none' }}>
                  <div style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: s.color || tk.text, fontFamily: s.mono ? '"JetBrains Mono", monospace' : 'inherit' }}>{s.value}</div>
                </div>
              ))}
          </div>

          {/* Filters */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${tk.border}`, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
            <div>
              <label style={labelStyle}>{t('pos.customerPhone')}</label>
              <input type="text" maxLength={15} value={sessionOrdersFilters.customer_phone}
                onChange={(e) => updateFilter('customer_phone', e.target.value.replace(/\D/g, ''))}
                placeholder={t('pos.searchByPhone')} style={{ ...inputStyle, height: 32 }} />
            </div>
            <div>
              <label style={labelStyle}>{t('pos.paymentMethod')}</label>
              <select value={sessionOrdersFilters.payment_method} onChange={(e) => updateFilter('payment_method', e.target.value)} style={{ ...selectStyle, height: 32 }}>
                <option value="all">{t('pos.allPaymentMethods')}</option>
                <option value="cash">{t('pos.cash')}</option>
                <option value="card">{t('pos.card')}</option>
                <option value="instapay">{t('pos.instapay')}</option>
                <option value="wallet">{t('pos.wallet')}</option>
                <option value="mixed">{t('pos.mixed')}</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('pos.orderMode')}</label>
              <select value={sessionOrdersFilters.order_type} onChange={(e) => updateFilter('order_type', e.target.value)} style={{ ...selectStyle, height: 32 }}>
                <option value="all">{t('pos.allOrderTypes')}</option>
                <option value="in_store">{t('pos.inStore')}</option>
                <option value="delivery">{t('pos.delivery')}</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('pos.deliveryPerson')}</label>
              <select value={sessionOrdersFilters.delivery_person_id} onChange={(e) => updateFilter('delivery_person_id', e.target.value)} style={{ ...selectStyle, height: 32 }}>
                <option value="all">{t('pos.allDeliveryPersons')}</option>
                {deliveryPersons.map((dp) => <option key={dp.id} value={dp.id}>{dp.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('common.status')}</label>
              <select value={sessionOrdersFilters.status} onChange={(e) => updateFilter('status', e.target.value)} style={{ ...selectStyle, height: 32 }}>
                <option value="all">{t('pos.allStatuses')}</option>
                <option value="completed">{t('pos.completed')}</option>
                <option value="refunded">{t('pos.refunded')}</option>
                <option value="partially_refunded">{t('pos.partiallyRefunded')}</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('common.from')}</label>
              <input type="datetime-local" value={sessionOrdersFilters.start_datetime} min={sessionStartLocal} max={nowLocal}
                onChange={(e) => updateFilter('start_datetime', e.target.value)} style={{ ...inputStyle, height: 32 }} />
            </div>
            <div>
              <label style={labelStyle}>{t('common.to')}</label>
              <input type="datetime-local" value={sessionOrdersFilters.end_datetime} min={sessionStartLocal} max={nowLocal}
                onChange={(e) => updateFilter('end_datetime', e.target.value)} style={{ ...inputStyle, height: 32 }} />
            </div>
          </div>

          {/* Content */}
          {sessionOrdersLoading ? (
            <div>
              {[0,1,2,3,4].map(i => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '11px 16px', alignItems: 'center', borderBottom: `1px solid ${tk.border}` }}>
                  {[1.4, 1, 0.4, 0.8, 0.7, 0.8, 0.35].map((w, j) => (
                    <div key={j} style={{ height: 13, borderRadius: 4, background: tk.surfaceAlt, width: `${Math.min(w * 70, 100)}%` }} />
                  ))}
                </div>
              ))}
            </div>
          ) : sessionOrdersError ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 12, padding: '10px 14px', borderRadius: 8, background: tk.dangerSoft, border: `1px solid ${tk.danger}33`, color: tk.dangerText, fontSize: 12.5 }}>
              <AlertCircle size={14} />
              {sessionOrdersError}
            </div>
          ) : filteredSessionOrders.length === 0 ? (
            <div style={{ padding: '52px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: tk.surfaceAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShoppingCart size={18} color={tk.textMuted} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>{t('pos.noSessionOrders')}</div>
              <div style={{ fontSize: 12, color: tk.textMuted }}>{language === 'ar' ? 'جرّب تعديل الفلاتر' : 'Try adjusting your filters'}</div>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '8px 16px', background: tk.surfaceAlt, borderBottom: `1px solid ${tk.border}`, fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                <div>{t('receipt.orderNo')}</div>
                <div>{t('common.date')}</div>
                <div>{t('receipt.items')}</div>
                <div>{t('pos.paymentMethod')}</div>
                <div>{t('common.total')}</div>
                <div>{t('common.status')}</div>
                <div>{language === 'ar' ? 'إجراء' : 'Actions'}</div>
              </div>
              {/* Table rows */}
              {pagedSessionOrders.map((order, index) => (
                <div key={order.id}
                  style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '10px 16px', alignItems: 'center', borderBottom: index < pagedSessionOrders.length - 1 ? `1px solid ${tk.border}` : 'none', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = tk.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tk.text, fontFamily: '"JetBrains Mono", monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.receipt_num || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: tk.textSecondary }}>
                    {new Date(order.created_at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tk.textSecondary, fontFamily: '"JetBrains Mono", monospace' }}>
                    {Array.isArray(order.items) ? order.items.length : 0}
                  </div>
                  <div><MethodPill tk={tk} method={order.payment_method || 'cash'} t={t} /></div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: tk.text, fontFamily: '"JetBrains Mono", monospace' }}>
                    {formatEGP(roundCurrency(order.total_amount || 0))}
                  </div>
                  <div>
                    <StatusPill tk={tk} kind={getStatusKind(order.status)}>
                      {order.status === 'completed' ? t('pos.completed')
                        : order.status === 'refunded' ? t('pos.refunded')
                        : order.status === 'partially_refunded' ? t('pos.partiallyRefunded')
                        : order.status || '—'}
                    </StatusPill>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <button type="button" onClick={() => handleViewSessionOrder(order)} title={t('common.view')}
                      style={iconBtn}
                      onMouseEnter={e => { e.currentTarget.style.background = tk.accentSoft; e.currentTarget.style.color = tk.accentText; }}
                      onMouseLeave={e => { e.currentTarget.style.background = tk.sunken; e.currentTarget.style.color = tk.textSecondary; }}>
                      <Eye size={13} />
                    </button>
                    <button type="button" onClick={() => handlePrintSessionOrder(order)} title={t('common.print')}
                      style={iconBtn}
                      onMouseEnter={e => { e.currentTarget.style.background = tk.surfaceAlt; e.currentTarget.style.color = tk.text; }}
                      onMouseLeave={e => { e.currentTarget.style.background = tk.sunken; e.currentTarget.style.color = tk.textSecondary; }}>
                      <Printer size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Pagination */}
          {filteredSessionOrders.length > SESSION_LIST_LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${tk.border}` }}>
              <div style={{ fontSize: 11.5, color: tk.textMuted }}>
                {language === 'ar' ? `صفحة ${sessionOrdersPage} من ${sessionOrdersTotalPages}` : `Page ${sessionOrdersPage} of ${sessionOrdersTotalPages}`}
              </div>
              <div style={{ display: 'flex', gap: 5 }}>
                <button type="button" onClick={() => setSessionOrdersPage(p => Math.max(1, p - 1))} disabled={sessionOrdersPage <= 1}
                  style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${tk.border}`, background: tk.surfaceAlt, color: tk.textSecondary, cursor: sessionOrdersPage <= 1 ? 'not-allowed' : 'pointer', opacity: sessionOrdersPage <= 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronLeft size={13} />
                </button>
                <button type="button" onClick={() => setSessionOrdersPage(p => Math.min(sessionOrdersTotalPages, p + 1))} disabled={sessionOrdersPage >= sessionOrdersTotalPages}
                  style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${tk.border}`, background: tk.surfaceAlt, color: tk.textSecondary, cursor: sessionOrdersPage >= sessionOrdersTotalPages ? 'not-allowed' : 'pointer', opacity: sessionOrdersPage >= sessionOrdersTotalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}

        {/* Order detail modal */}
        {viewOrderDetail && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}
            onClick={() => setViewOrderDetail(null)}>
            <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, maxWidth: 560, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: `1px solid ${tk.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 7, background: tk.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShoppingCart size={14} color={tk.accentText} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: tk.text }}>{language === 'ar' ? 'تفاصيل الطلب' : 'Order Details'}</div>
                    <div style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono", monospace', marginTop: 1 }}>
                      {viewOrderDetail.receipt_num || viewOrderDetail.id?.slice(0, 8) || '—'}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => setViewOrderDetail(null)}
                  style={{ width: 28, height: 28, borderRadius: 6, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <X size={13} />
                </button>
              </div>

              {/* Modal body */}
              <div style={{ overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                {loadingOrderDetail ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[0.8, 0.6, 1, 0.7].map((w, i) => (
                      <div key={i} style={{ height: 14, borderRadius: 4, background: tk.surfaceAlt, width: `${w * 80}%` }} />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Key info */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                      {[
                        { label: language === 'ar' ? 'رقم الوصل' : 'Receipt No', value: viewOrderDetail.receipt_num || '—', mono: true, copyable: true },
                        { label: language === 'ar' ? 'التاريخ' : 'Date', value: viewOrderDetail.created_at ? new Date(viewOrderDetail.created_at).toLocaleString() : '—' },
                        { label: language === 'ar' ? 'طريقة الدفع' : 'Payment Method', value: viewOrderDetail.payment_method || '—' },
                        { label: language === 'ar' ? 'الحالة' : 'Status', value: viewOrderDetail.status === 'completed' ? t('pos.completed') : viewOrderDetail.status === 'refunded' ? t('pos.refunded') : viewOrderDetail.status === 'partially_refunded' ? t('pos.partiallyRefunded') : viewOrderDetail.status || '—' },
                        ...(parseFloat(viewOrderDetail.promo_percentage || 0) > 0
                          ? [{
                              label: t('pos.promo'),
                              value: viewOrderDetail.promo_name
                                ? `${viewOrderDetail.promo_name} (${viewOrderDetail.promo_percentage}%)`
                                : `${viewOrderDetail.promo_percentage}%`,
                              mono: false,
                              color: tk.warningText,
                            }]
                          : []),
                        ...(parseFloat(viewOrderDetail.discount_percentage || 0) > 0
                          ? [{
                              label: language === 'ar' ? 'الخصم' : 'Discount',
                              value: `${viewOrderDetail.discount_percentage}%`,
                              mono: false,
                              color: tk.warningText,
                            }]
                          : []),
                        ...(parseFloat(viewOrderDetail.discount_percentage || 0) > 0 && viewOrderDetail.discount_notes
                          ? [{ label: t('pos.discountNotes'), value: viewOrderDetail.discount_notes, mono: false, color: tk.textSecondary }]
                          : []),
                        ...(parseFloat(viewOrderDetail.discount_percentage || 0) > 0 && viewOrderDetail.discount_approver_name
                          ? [{
                              label: t('refunds.approvedBy'),
                              value: viewOrderDetail.discount_approver_role
                                ? `${viewOrderDetail.discount_approver_name} (${viewOrderDetail.discount_approver_role})`
                                : viewOrderDetail.discount_approver_name,
                              mono: false,
                              color: tk.accentText,
                            }]
                          : []),
                        ...(viewOrderDetail.tax_percentage != null && Number(viewOrderDetail.tax_percentage) > 0
                          ? [{
                              label: language === 'ar' ? 'الضريبة' : 'Tax',
                              value: `${viewOrderDetail.tax_percentage}%`,
                              mono: false,
                              color: tk.accentText,
                            }]
                          : []),
                        { label: language === 'ar' ? 'المبلغ الإجمالي' : 'Total Amount', value: formatEGP(roundCurrency(viewOrderDetail.total_amount || 0)), mono: true, color: tk.successText, span2: true },
                      ].map(({ label, value, mono, color, copyable, span2 }, i) => (
                        <div key={i} style={span2 ? { gridColumn: '1 / -1' } : undefined}>
                          <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: color || tk.text, fontFamily: mono ? '"JetBrains Mono", monospace' : 'inherit' }}>{value}</div>
                            {copyable && value !== '—' && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(value);
                                  setCopiedOrderReceiptNo(true);
                                  setTimeout(() => setCopiedOrderReceiptNo(false), 2000);
                                }}
                                title={language === 'ar' ? 'نسخ' : 'Copy'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: copiedOrderReceiptNo ? tk.accent : tk.textMuted, display: 'flex', alignItems: 'center', borderRadius: 4, transition: 'color 0.2s' }}
                              >
                                {copiedOrderReceiptNo ? <Check size={13} /> : <Copy size={13} />}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Delivery details */}
                    {viewOrderDetail.delivery_region && (() => {
                      const match = (viewOrderDetail.delivery_region || '').match(/^(.+?)\s*\(([0-9,]+\.?[0-9]*)\)\s*$/);
                      const regionLabel = match ? match[1].trim() : viewOrderDetail.delivery_region;
                      const deliveryFee = match ? parseFloat(match[2].replace(/,/g, '')) : null;
                      const rows = [
                        ...(viewOrderDetail.customer_name ? [{ label: language === 'ar' ? 'اسم العميل' : 'Customer', value: viewOrderDetail.customer_name }] : []),
                        ...(viewOrderDetail.customer_phone ? [{ label: language === 'ar' ? 'رقم الهاتف' : 'Phone', value: viewOrderDetail.customer_phone, mono: true }] : []),
                        ...(viewOrderDetail.customer_address ? [{ label: language === 'ar' ? 'العنوان' : 'Address', value: viewOrderDetail.customer_address, span2: true }] : []),
                        { label: language === 'ar' ? 'المنطقة' : 'Region', value: regionLabel || '—' },
                        ...(deliveryFee != null ? [{ label: language === 'ar' ? 'رسوم التوصيل' : 'Delivery Fee', value: formatEGP(roundCurrency(deliveryFee)), mono: true }] : []),
                        ...(viewOrderDetail.delivery_person_name ? [{ label: language === 'ar' ? 'المندوب' : 'Delivery Person', value: viewOrderDetail.delivery_person_name }] : []),
                      ];
                      return (
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                            {language === 'ar' ? 'التوصيل' : 'Delivery'}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                            {rows.map(({ label, value, mono, span2 }, i) => (
                              <div key={i} style={span2 ? { gridColumn: '1 / -1' } : undefined}>
                                <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: tk.text, fontFamily: mono ? '"JetBrains Mono", monospace' : 'inherit' }}>{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Order items */}
                    {Array.isArray(viewOrderDetail.items) && viewOrderDetail.items.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                          {language === 'ar' ? 'المنتجات' : 'Items'}
                        </div>
                        <div style={{ border: `1px solid ${tk.border}`, borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 65px 80px', padding: '8px 12px', background: tk.surfaceAlt, borderBottom: `1px solid ${tk.border}`, fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            <div>{language === 'ar' ? 'المنتج' : 'Product'}</div>
                            <div style={{ textAlign: 'right' }}>{language === 'ar' ? 'الكمية' : 'Qty'}</div>
                            <div style={{ textAlign: 'right' }}>{language === 'ar' ? 'المبلغ' : 'Amount'}</div>
                          </div>
                          {viewOrderDetail.items.map((item, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 65px 80px', padding: '9px 12px', alignItems: item.is_offer ? 'flex-start' : 'center', borderBottom: i < viewOrderDetail.items.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: tk.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_name || '—'}</div>
                                {item.is_offer && (
                                  <OrderItemComponentsList
                                    components={item.components}
                                    tk={tk}
                                    t={t}
                                  />
                                )}
                              </div>
                              <div style={{ textAlign: 'right', fontSize: 12, color: tk.textSecondary, fontFamily: '"JetBrains Mono", monospace' }}>{parseFloat(item.quantity || 0).toLocaleString()}</div>
                              <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, color: tk.text, fontFamily: '"JetBrains Mono", monospace' }}>{formatEGP(roundCurrency(item.total_price || 0))}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '11px 16px', borderTop: `1px solid ${tk.border}`, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                <button type="button" onClick={() => handlePrintSessionOrder(viewOrderDetail)}
                  style={{ height: 32, padding: '0 14px', borderRadius: 7, background: tk.accentSoft, border: `1px solid ${tk.accentText}33`, color: tk.accentText, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Printer size={13} />
                  {t('common.print')}
                </button>
                <button type="button" onClick={() => setViewOrderDetail(null)}
                  style={{ height: 32, padding: '0 14px', borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500 }}>
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Receipt modal — must live here since this page has an early return */}
        {previewReceipt && !showSessionOrdersPage && !showSessionRefundsPage && (
          <Receipt
            transaction={previewReceipt}
            storeName={storeName}
            receiptFooter={receiptFooter}
            website={website}
            phoneNumbers={phoneNumbers}
            socialMediaUrls={socialMediaUrls}
            logoPath={logoPath}
            logoSize={logoSize}
            includeDeliveryFee={includeDeliveryFee}
            onClose={() => setPreviewReceipt(null)}
          />
        )}
      </div>
    );
  }

  if (showSessionRefundsPage) {
    const totalRefunded = sessionRefunds.reduce(
      (s, r) => s + (Number(r.amount) || 0),
      0
    );
    const partialCount = sessionRefunds.filter(r => r.order_status === 'partially_refunded').length;

    const RCOL = 'minmax(120px, 1fr) 145px minmax(80px, 1.5fr) 108px 130px 100px 96px';

    const iconBtn = {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 28, height: 28, borderRadius: 6,
      background: tk.sunken, border: `1px solid ${tk.border}`,
      color: tk.textSecondary, cursor: 'pointer', transition: 'background 0.12s, color 0.12s',
    };

    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: tk.bg, overflow: 'auto', fontFamily: 'DM Sans, sans-serif', padding: 14 }} dir={isRTL ? 'rtl' : 'ltr'}>
        <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, maxWidth: 1200, margin: '0 auto', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: `1px solid ${tk.border}`, gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: tk.dangerSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <RotateCcw size={15} color={tk.dangerText} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: tk.text, lineHeight: 1.2 }}>{t('pos.sessionRefunds')}</div>
                <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 2 }}>{sessionRefunds.length} {language === 'ar' ? 'مرتجع' : 'refunds'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" onClick={() => setSessionRefundsFilters({ receipt_num: '', order_status: 'all', approved_by: 'all' })}
                style={{ height: 30, padding: '0 10px', borderRadius: 7, border: `1px solid ${tk.border}`, background: 'transparent', color: tk.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }}>
                {t('common.reset')}
              </button>
              <button type="button" onClick={loadSessionRefunds} disabled={sessionRefundsLoading}
                style={{ height: 30, padding: '0 10px', borderRadius: 7, border: `1px solid ${tk.border}`, background: tk.surfaceAlt, color: tk.textSecondary, cursor: sessionRefundsLoading ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', opacity: sessionRefundsLoading ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <RefreshCw size={12} />
                {t('common.refresh')}
              </button>
              <button type="button" onClick={() => setShowSessionRefundsPage(false)}
                style={{ height: 30, padding: '0 10px', borderRadius: 7, border: `1px solid ${tk.border}`, background: 'transparent', color: tk.textSecondary, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <X size={13} />
                {t('common.back')}
              </button>
            </div>
          </div>

          {/* Summary stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: `1px solid ${tk.border}` }}>
            {[
              { label: language === 'ar' ? 'إجمالي المرتجعات' : 'Total Refunds', value: sessionRefunds.length, mono: true },
              { label: language === 'ar' ? 'إجمالي المبلغ المرتجع' : 'Total Refunded', value: formatEGP(roundCurrency(totalRefunded)), mono: true, color: tk.dangerText },
              { label: language === 'ar' ? 'مرتجع جزئي' : 'Partial Refunds', value: partialCount, mono: true, color: tk.warning },
            ].map((s, i) => (
              <div key={i} style={{ padding: '10px 16px', borderInlineEnd: i < 2 ? `1px solid ${tk.border}` : 'none' }}>
                <div style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: s.color || tk.text, fontFamily: s.mono ? '"JetBrains Mono", monospace' : 'inherit' }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${tk.border}`, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            <div {...sessionRefundsReceiptSearchProps}>
              <label style={labelStyle}>{t('receipt.receiptNo')}</label>
              <input
                type="text"
                name="sessionRefundsReceiptFilter"
                inputMode="text"
                maxLength={50}
                value={sessionRefundsFilters.receipt_num}
                onChange={(e) => setSessionRefundsFilters(f => ({ ...f, receipt_num: normalizeReceiptNumInput(e.target.value) }))}
                placeholder={t('refunds.searchByReceiptNo')}
                dir="ltr"
                lang="en"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                style={{ ...inputStyle, height: 32 }}
              />
            </div>
            <div>
              <label style={labelStyle}>{t('common.status')}</label>
              <select value={sessionRefundsFilters.order_status}
                onChange={(e) => setSessionRefundsFilters(f => ({ ...f, order_status: e.target.value }))}
                style={{ ...selectStyle, height: 32 }}>
                <option value="all">{t('pos.allStatuses')}</option>
                <option value="refunded">{t('pos.refunded')}</option>
                <option value="partially_refunded">{t('pos.partiallyRefunded')}</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>{t('refunds.approvedBy')}</label>
              <select value={sessionRefundsFilters.approved_by}
                onChange={(e) => setSessionRefundsFilters(f => ({ ...f, approved_by: e.target.value }))}
                style={{ ...selectStyle, height: 32 }}>
                <option value="all">{t('common.all')}</option>
                {sessionRefundApprovers.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
          </div>

          {/* Content */}
          {sessionRefundsLoading ? (
            <div>
              {[0,1,2,3,4].map(i => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: RCOL, gap: 8, padding: '11px 16px', alignItems: 'center', borderBottom: `1px solid ${tk.border}` }}>
                  {[1, 0.8, 1.2, 0.6, 0.9, 0.7, 0.3].map((w, j) => (
                    <div key={j} style={{ height: 13, borderRadius: 4, background: tk.surfaceAlt, width: `${Math.min(w * 70, 100)}%` }} />
                  ))}
                </div>
              ))}
            </div>
          ) : sessionRefundsError ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 12, padding: '10px 14px', borderRadius: 8, background: tk.dangerSoft, border: `1px solid ${tk.danger}33`, color: tk.dangerText, fontSize: 12.5 }}>
              <AlertCircle size={14} />
              {sessionRefundsError}
            </div>
          ) : sessionRefunds.length === 0 ? (
            <div style={{ padding: '52px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: tk.surfaceAlt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <RotateCcw size={18} color={tk.textMuted} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>{t('pos.noSessionRefunds')}</div>
              <div style={{ fontSize: 12, color: tk.textMuted }}>{language === 'ar' ? 'لا يوجد مرتجعات لهذه الجلسة' : 'No refunds for this session'}</div>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div style={{ display: 'grid', gridTemplateColumns: RCOL, gap: 8, padding: '8px 16px', background: tk.surfaceAlt, borderBottom: `1px solid ${tk.border}`, fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                <div>{t('receipt.orderNo')}</div>
                <div>{t('common.date')}</div>
                <div>{t('refunds.refundReason')}</div>
                <div>{t('common.amount')}</div>
                <div>{t('refunds.approvedBy')}</div>
                <div>{t('common.status')}</div>
                <div>{language === 'ar' ? 'إجراء' : 'Actions'}</div>
              </div>
              {/* Table rows */}
              {pagedSessionRefunds.map((refund, index) => (
                <div key={refund.id}
                  style={{ display: 'grid', gridTemplateColumns: RCOL, gap: 8, padding: '10px 16px', alignItems: 'center', borderBottom: index < pagedSessionRefunds.length - 1 ? `1px solid ${tk.border}` : 'none', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = tk.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: tk.text, fontFamily: '"JetBrains Mono", monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {refund.receipt_num || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: tk.textSecondary }}>
                    {new Date(refund.created_at).toLocaleString('en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ fontSize: 12, color: tk.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {refund.reason || '—'}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: tk.dangerText, fontFamily: '"JetBrains Mono", monospace' }}>
                    {formatEGP(roundCurrency(refund.amount || 0))}
                  </div>
                  <div style={{ fontSize: 12, color: tk.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {refund.approver_name || '—'}
                  </div>
                  <div>
                    <StatusPill tk={tk} kind={refund.order_status === 'partially_refunded' ? 'pending' : 'refunded'}>
                      {refund.order_status === 'refunded' ? t('pos.refunded')
                        : refund.order_status === 'partially_refunded' ? t('pos.partiallyRefunded')
                        : refund.order_status || '—'}
                    </StatusPill>
                  </div>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    <button type="button" onClick={() => handleViewSessionRefund(refund)} title={t('common.view')}
                      style={iconBtn}
                      onMouseEnter={e => { e.currentTarget.style.background = tk.dangerSoft; e.currentTarget.style.color = tk.dangerText; }}
                      onMouseLeave={e => { e.currentTarget.style.background = tk.sunken; e.currentTarget.style.color = tk.textSecondary; }}>
                      <Eye size={13} />
                    </button>
                    <button type="button" onClick={() => handlePrintSessionRefund(refund)} title={t('common.print')}
                      style={iconBtn}
                      onMouseEnter={e => { e.currentTarget.style.background = tk.accentSoft || tk.surfaceAlt; e.currentTarget.style.color = tk.accentText || tk.text; }}
                      onMouseLeave={e => { e.currentTarget.style.background = tk.sunken; e.currentTarget.style.color = tk.textSecondary; }}>
                      <Printer size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Pagination */}
          {sessionRefunds.length > SESSION_LIST_LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${tk.border}` }}>
              <div style={{ fontSize: 11.5, color: tk.textMuted }}>
                {language === 'ar' ? `صفحة ${sessionRefundsPage} من ${sessionRefundsTotalPages}` : `Page ${sessionRefundsPage} of ${sessionRefundsTotalPages}`}
              </div>
              <div style={{ display: 'flex', gap: 5 }}>
                <button type="button" onClick={() => setSessionRefundsPage(p => Math.max(1, p - 1))} disabled={sessionRefundsPage <= 1}
                  style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${tk.border}`, background: tk.surfaceAlt, color: tk.textSecondary, cursor: sessionRefundsPage <= 1 ? 'not-allowed' : 'pointer', opacity: sessionRefundsPage <= 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronLeft size={13} />
                </button>
                <button type="button" onClick={() => setSessionRefundsPage(p => Math.min(sessionRefundsTotalPages, p + 1))} disabled={sessionRefundsPage >= sessionRefundsTotalPages}
                  style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${tk.border}`, background: tk.surfaceAlt, color: tk.textSecondary, cursor: sessionRefundsPage >= sessionRefundsTotalPages ? 'not-allowed' : 'pointer', opacity: sessionRefundsPage >= sessionRefundsTotalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Refund detail modal */}
        {viewRefundDetail && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}
            onClick={() => setViewRefundDetail(null)}>
            <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, maxWidth: 560, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
              onClick={e => e.stopPropagation()}>

              {/* Modal header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: `1px solid ${tk.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 7, background: tk.dangerSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <RotateCcw size={14} color={tk.dangerText} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: tk.text }}>{language === 'ar' ? 'تفاصيل المرتجع' : 'Refund Details'}</div>
                    <div style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono", monospace', marginTop: 1 }}>
                      {viewRefundDetail.receipt_num || viewRefundDetail.id?.slice(0, 8) || '—'}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => setViewRefundDetail(null)}
                  style={{ width: 28, height: 28, borderRadius: 6, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <X size={13} />
                </button>
              </div>

              {/* Modal body */}
              <div style={{ overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                {loadingRefundDetail ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[0.8, 0.6, 1, 0.7].map((w, i) => (
                      <div key={i} style={{ height: 14, borderRadius: 4, background: tk.surfaceAlt, width: `${w * 80}%` }} />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Key info */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, background: tk.surfaceAlt, borderRadius: 10, padding: 14, border: `1px solid ${tk.border}` }}>
                      {[
                        { label: language === 'ar' ? 'رقم الوصل' : 'Receipt No', value: viewRefundDetail.receipt_num || '—', mono: true, copyable: true },
                        { label: language === 'ar' ? 'التاريخ' : 'Date', value: viewRefundDetail.created_at ? new Date(viewRefundDetail.created_at).toLocaleString() : '—' },
                        { label: language === 'ar' ? 'المبلغ المرتجع' : 'Refund Amount', value: formatEGP(roundCurrency(viewRefundDetail.amount || 0)), mono: true, color: tk.dangerText },
                        { label: language === 'ar' ? 'المعتمد بواسطة' : 'Approved By', value: viewRefundDetail.approver_name || '—' },
                      ].map(({ label, value, mono, color, copyable }, i) => (
                        <div key={i}>
                          <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: color || tk.text, fontFamily: mono ? '"JetBrains Mono", monospace' : 'inherit' }}>{value}</div>
                            {copyable && value !== '—' && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(value);
                                  setCopiedRefundReceiptNo(true);
                                  setTimeout(() => setCopiedRefundReceiptNo(false), 2000);
                                }}
                                title={language === 'ar' ? 'نسخ' : 'Copy'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: copiedRefundReceiptNo ? tk.accent : tk.textMuted, display: 'flex', alignItems: 'center', borderRadius: 4, transition: 'color 0.2s' }}
                              >
                                {copiedRefundReceiptNo ? <Check size={13} /> : <Copy size={13} />}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {viewRefundDetail.reason && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{t('refunds.refundReason')}</div>
                          <div style={{ fontSize: 13, color: tk.text }}>{viewRefundDetail.reason}</div>
                        </div>
                      )}
                    </div>

                    {/* Refunded items */}
                    {Array.isArray(viewRefundDetail.items) && viewRefundDetail.items.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                          {language === 'ar' ? 'المنتجات المرتجعة' : 'Refunded Items'}
                        </div>
                        <div style={{ border: `1px solid ${tk.border}`, borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 65px 80px 70px', padding: '8px 12px', background: tk.surfaceAlt, borderBottom: `1px solid ${tk.border}`, fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            <div>{language === 'ar' ? 'المنتج' : 'Product'}</div>
                            <div style={{ textAlign: 'right' }}>{language === 'ar' ? 'الكمية' : 'Qty'}</div>
                            <div style={{ textAlign: 'right' }}>{language === 'ar' ? 'المبلغ' : 'Amount'}</div>
                            <div style={{ textAlign: 'right' }}>{language === 'ar' ? 'مخزون' : 'Restock'}</div>
                          </div>
                          {viewRefundDetail.items.map((item, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 65px 80px 70px', padding: '9px 12px', alignItems: 'center', borderBottom: i < viewRefundDetail.items.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: tk.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_name || '—'}</div>
                              <div style={{ textAlign: 'right', fontSize: 12, color: tk.textSecondary, fontFamily: '"JetBrains Mono", monospace' }}>{parseFloat(item.refunded_quantity || 0).toLocaleString()}</div>
                              <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, color: tk.dangerText, fontFamily: '"JetBrains Mono", monospace' }}>{formatEGP(roundCurrency(item.refunded_amount || 0))}</div>
                              <div style={{ textAlign: 'right' }}>
                                <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: item.restock ? tk.successSoft : tk.sunken, color: item.restock ? tk.successText : tk.textMuted }}>
                                  {item.restock ? t('common.yes') : t('common.no')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '11px 16px', borderTop: `1px solid ${tk.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button"
                  onClick={() => handlePrintSessionRefund(viewRefundDetail)}
                  disabled={loadingRefundDetail}
                  style={{ height: 32, padding: '0 14px', borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.text, cursor: loadingRefundDetail ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: loadingRefundDetail ? 0.6 : 1 }}>
                  <Printer size={13} />
                  {t('common.print')}
                </button>
                <button type="button" onClick={() => setViewRefundDetail(null)}
                  style={{ height: 32, padding: '0 14px', borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500 }}>
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Main POS Layout ──────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 390px',
        gap: 14,
        padding: 14,
        height: '100%',
        background: tk.bg,
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontFamily: 'DM Sans, sans-serif',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* ── LEFT PANEL ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {/* Order tabs + mode toggle row */}
        <div
          ref={orderTabsRef}
          className="pos-order-tabs-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {/* Order tabs */}
          {pos.orders.map((o) => {
            const isActive = pos.activeOrderId === o.id;
            const itemCount = (o.cart || []).length;
            return (
              <div
                key={o.id}
                onClick={() => dispatch(switchOrder(o.id))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 13px',
                  height: 36,
                  background: isActive ? tk.surface : 'transparent',
                  border: isActive
                    ? `1px solid ${tk.borderStrong}`
                    : '1px solid transparent',
                  borderTop: isActive ? `2px solid ${tk.accent}` : '2px solid transparent',
                  borderRadius: 9,
                  cursor: 'pointer',
                  fontSize: 12.5,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? tk.text : tk.textSecondary,
                  flexShrink: 0,
                  transition: 'all 0.15s',
                  userSelect: 'none',
                  boxShadow: isActive ? tk.shadow : 'none',
                }}
              >
                <span>{o.id}</span>
                {itemCount > 0 && (
                  <span
                    style={{
                      background: isActive ? tk.accentSoft : tk.sunken,
                      color: isActive ? tk.accent : tk.textMuted,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 99,
                      fontFamily: '"JetBrains Mono", monospace',
                    }}
                  >
                    {itemCount}
                  </span>
                )}
                {pos.orders.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(closeOrder(o.id));
                    }}
                    style={{
                      width: 16,
                      height: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      color: tk.textMuted,
                      padding: 0,
                      flexShrink: 0,
                    }}
                    title="Close order"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            );
          })}

          {/* New sale button */}
          {pos.orders.length < MAX_ORDERS && (
            <button
              onClick={() => dispatch(createNewOrder())}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '0 12px',
                height: 36,
                background: 'transparent',
                border: `1px dashed ${tk.borderStrong}`,
                borderRadius: 8,
                color: tk.textSecondary,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                flexShrink: 0,
              }}
            >
              <Plus size={12} />
              {t('pos.newSale')}
            </button>
          )}

        </div>

        {/* Delivery panel */}
        {activeOrder.mode === 'delivery' && (
          <div
            style={{
              background: tk.surface,
              border: `1px solid ${tk.border}`,
              borderRadius: 12,
              padding: 12,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 10,
              }}
            >
              <div>
                <label style={labelStyle}>{t('pos.customerPhone')}</label>
                <input
                  type="text"
                  maxLength={15}
                  value={delivery.phone}
                  onChange={(e) =>
                    handleDeliveryPhoneChange(e.target.value.replace(/\D/g, ''))
                  }
                  style={{ ...inputStyle, height: 34 }}
                  placeholder="01012345678"
                />
              </div>
              <div>
                <label style={labelStyle}>{t('pos.customerName')}</label>
                <input
                  type="text"
                  maxLength={30}
                  value={delivery.name}
                  onChange={(e) => dispatch(setDeliveryName(e.target.value))}
                  style={{ ...inputStyle, height: 34 }}
                  placeholder={t('pos.customerName')}
                />
              </div>
              <div>
                <label style={labelStyle}>{t('pos.customerAddress')}</label>
                <input
                  type="text"
                  maxLength={50}
                  value={delivery.address}
                  onChange={(e) => dispatch(setDeliveryAddress(e.target.value))}
                  style={{ ...inputStyle, height: 34 }}
                  placeholder={t('pos.customerAddress')}
                />
              </div>
              <div>
                <label style={labelStyle}>{t('pos.deliveryRegion')}</label>
                <select
                  value={selectedDeliveryRegion}
                  onChange={(e) => dispatch(setDeliveryRegion(e.target.value))}
                  style={selectStyle}
                  className={isRTL ? 'rtl-select' : 'ltr-select'}
                >
                  <option value="">{t('pos.selectRegion')}</option>
                  {deliveryRegions.map((r) => (
                    <option key={r.region} value={r.region}>
                      {r.region} (+{formatEGP(r.fee)})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{t('pos.deliveryPerson')}</label>
                <select
                  value={delivery.deliveryPersonId || ''}
                  onChange={(e) => dispatch(setDeliveryPersonId(e.target.value || null))}
                  style={selectStyle}
                  className={isRTL ? 'rtl-select' : 'ltr-select'}
                >
                  <option value="">{t('pos.selectDeliveryPerson')}</option>
                  {deliveryPersons.map((dp) => (
                    <option key={dp.id} value={dp.id}>
                      {dp.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Search input */}
        <div
          style={{
            background: tk.sunken,
            border: `1px solid ${tk.border}`,
            borderRadius: 9,
            height: 38,
            padding: '0 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke={tk.textMuted}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={searchInputRef}
            type="text"
            maxLength={50}
            placeholder={t('pos.searchProducts')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: tk.text,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: tk.textMuted,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Category chips */}
        <div
          ref={catRowRef}
          className="pos-cat-row"
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'nowrap',
            flexShrink: 0,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <button
            onClick={() => dispatch(setSelectedCategory(null))}
            onMouseDown={(e) => e.preventDefault()}
            style={{
              padding: '5px 14px',
              height: 30,
              borderRadius: 99,
              border: !selectedCategory
                ? `1px solid ${tk.accent}`
                : `1px solid ${tk.border}`,
              background: !selectedCategory ? tk.accent : 'transparent',
              color: !selectedCategory ? '#fff' : tk.textSecondary,
              fontSize: 11.5,
              fontWeight: !selectedCategory ? 600 : 400,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {t('pos.allCategories')}
          </button>
          {activeOffers.length > 0 && (() => {
            const offersActive = selectedCategory === OFFERS_CATEGORY_ID;
            const offersColor = '#5f81c4';
            return (
              <button
                onClick={() => dispatch(setSelectedCategory(OFFERS_CATEGORY_ID))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 14px',
                  height: 30,
                  borderRadius: 99,
                  border: offersActive
                    ? `1px solid ${offersColor}`
                    : `1px solid ${tk.border}`,
                  background: offersActive ? offersColor : 'transparent',
                  color: offersActive ? '#fff' : tk.textSecondary,
                  fontSize: 11.5,
                  fontWeight: offersActive ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <Tags
                  size={13}
                  color={offersActive ? '#fff' : offersColor}
                  style={{ flexShrink: 0 }}
                />
                {t('pos.offersCategory')}
              </button>
            );
          })()}
          {categories.map((category, idx) => {
            const isActive = selectedCategory === category.id;
            const catColor = category.color || (tk.cat ? tk.cat[idx % tk.cat.length] : tk.accent);
            return (
              <button
                key={category.id}
                onClick={() => dispatch(setSelectedCategory(category.id))}
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 14px',
                  height: 30,
                  borderRadius: 99,
                  border: isActive
                    ? `1px solid ${catColor}`
                    : `1px solid ${tk.border}`,
                  background: isActive ? catColor : 'transparent',
                  color: isActive ? '#fff' : tk.textSecondary,
                  fontSize: 11.5,
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {!isActive && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: catColor,
                      flexShrink: 0,
                    }}
                  />
                )}
                {category.name}
              </button>
            );
          })}
        </div>

        {/* Product grid */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            paddingRight: 8,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 10,
            }}
          >
            {sortedFilteredItems.map((item) => {
              const available = getAvailableStock(item);
              const isLowStock = available > 0 && available < lowStockThreshold;
              const isOutOfStock = available <= 0;
              const blockedByStock = isOutOfStock && !allowNegativeStock;
              return (
                <div
                  key={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddToCart(item);
                  }}
                  style={{
                    background: tk.surface,
                    border: `1px solid ${tk.border}`,
                    borderRadius: 14,
                    padding: 12,
                    cursor: blockedByStock ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    opacity: blockedByStock ? 0.5 : 1,
                    transition: 'border-color 0.15s, transform 0.12s, box-shadow 0.15s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  }}
                  onMouseEnter={(e) => {
                    if (!blockedByStock) {
                      e.currentTarget.style.borderColor = tk.borderStrong;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = tk.shadow;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = tk.border;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!blockedByStock) e.currentTarget.style.transform = 'scale(0.97)';
                  }}
                  onMouseUp={(e) => {
                    if (!blockedByStock) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                >
                  {/* Low stock badge */}
                  {isLowStock && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 8,
                        insetInlineEnd: 8,
                        background: tk.warningSoft,
                        color: tk.warning,
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: '2px 7px',
                        borderRadius: 99,
                        letterSpacing: 0.3,
                        border: `1px solid ${tk.warning}30`,
                      }}
                    >
                      Low
                    </div>
                  )}
                  {/* Recorded stock is 0/negative but selling is still allowed - warn, don't block */}
                  {isOutOfStock && allowNegativeStock && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 8,
                        insetInlineEnd: 8,
                        background: tk.dangerSoft,
                        color: tk.dangerText,
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: '2px 7px',
                        borderRadius: 99,
                        letterSpacing: 0.3,
                        border: `1px solid ${tk.dangerText}30`,
                      }}
                      title={t('alerts.sellingWithoutStock').replace('{name}', item.name)}
                    >
                      {t('pos.noRecordedStock')}
                    </div>
                  )}
                  {/* Product image / placeholder */}
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      background: tk.sunken,
                      borderRadius: 10,
                      marginBottom: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {item.image_url ? (
                      <img
                        src={getImageUrl(item.image_url)}
                        alt={item.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Package size={22} style={{ color: tk.textMuted }} />
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: tk.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: 4,
                      lineHeight: 1.35,
                    }}
                  >
                    {item.name}
                  </div>
                  {item.sku && (
                    <div
                      style={{
                        fontSize: 10.5,
                        color: tk.textMuted,
                        fontFamily: '"JetBrains Mono", monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginBottom: 4,
                      }}
                    >
                      {item.sku}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: tk.accent,
                        fontFamily: '"JetBrains Mono", monospace',
                      }}
                    >
                      {formatEGP(item.price || item.selling_price || 0)}
                    </div>
                    <div style={{ fontSize: 10.5, color: tk.textMuted }}>
                      {item.unit_type === 'weight'
                        ? formatQuantity(available)
                        : available}{' '}
                      {item.unit_type === 'weight' ? 'kg' : 'pcs'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL (cart) ──────────────────────────────────────────────────── */}
      <div
        style={{
          background: tk.surface,
          border: `1px solid ${tk.border}`,
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
          boxShadow: tk.shadow,
        }}
      >
        {/* Cart header */}
        <div
          style={{
            padding: '14px 16px 13px',
            borderBottom: `1px solid ${tk.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <ShoppingCart size={16} style={{ color: tk.textSecondary }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: tk.text, flex: 1 }}>
            {t('pos.cart')}
            <span
              style={{
                marginInlineStart: 6,
                background: tk.sunken,
                color: tk.textMuted,
                fontSize: 10.5,
                fontFamily: '"JetBrains Mono", monospace',
                padding: '1px 7px',
                borderRadius: 99,
                fontWeight: 600,
              }}
            >
              {cartLinesCount}
            </span>
          </span>

          {/* ── In-store / Delivery mode toggle ─────────────────── */}
          {session?.status === 'open' && (
            <div style={{ background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 8, padding: 3, display: 'flex', gap: 2, flexShrink: 0, marginInlineEnd: 8 }}>
              {[
                { m: 'in_store', label: language === 'ar' ? 'داخلي' : 'In-store' },
                { m: 'delivery', label: language === 'ar' ? 'توصيل' : t('pos.delivery') },
              ].map(({ m, label }) => {
                const isActive = activeOrder.mode === m || (m === 'in_store' && !activeOrder.mode);
                return (
                  <button key={m}
                    onClick={() => {
                      if (m === 'delivery') { dispatch(setOrderMode('delivery')); }
                      else { dispatch(setOrderMode('in_store')); dispatch(setDeliveryPhone('')); dispatch(setDeliveryName('')); dispatch(setDeliveryAddress('')); dispatch(setDeliveryRegion('')); dispatch(setDeliveryPersonId(null)); }
                    }}
                    style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: isActive ? tk.accent : 'transparent', color: isActive ? '#fff' : tk.textSecondary, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {cart.length > 0 && (
            <button
              onClick={() => {
                dispatch(clearOrderCart(pos.activeOrderId));
                focusBarcodeInput();
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: tk.textMuted,
                cursor: 'pointer',
                fontSize: 11,
                fontFamily: 'inherit',
                padding: '3px 8px',
                borderRadius: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Trash2 size={11} />
              Clear
            </button>
          )}
        </div>

        {/* Barcode input */}
        <div style={{ padding: '8px 12px 10px', borderBottom: `1px solid ${tk.border}`, flexShrink: 0 }}>
          <form onSubmit={handleBarcodeSubmit}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: tk.sunken,
              border: `1px solid ${tk.border}`,
              borderRadius: 8,
              padding: '0 10px',
              height: 36,
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={tk.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 5v4M3 15v4M7 3H5a2 2 0 0 0-2 2v2M17 3h2a2 2 0 0 1 2 2v2M7 21H5a2 2 0 0 1-2-2v-2M17 21h2a2 2 0 0 0 2-2v-2"/>
                <line x1="7" y1="8" x2="7" y2="16"/><line x1="11" y1="8" x2="11" y2="16"/>
                <line x1="15" y1="8" x2="15" y2="16"/>
              </svg>
              <input
                ref={barcodeInputRef}
                type="text"
                maxLength={70}
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder={t('pos.scanBarcode')}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: tk.text,
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
                autoFocus
              />
            </div>
          </form>
        </div>

        {/* Cart lines */}
        <div
          ref={cartScrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            minHeight: 0,
          }}
        >
          {cart.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 16,
                  background: tk.sunken,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShoppingCart size={24} style={{ color: tk.textMuted }} />
              </div>
              <span style={{ color: tk.textMuted, fontSize: 13 }}>{t('pos.emptyCart')}</span>
            </div>
          ) : (
            cart.map((item, index) => (
              <div
                key={item.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 8,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: tk.surfaceAlt,
                  border: `1px solid ${tk.border}`,
                }}
              >
                {/* Name + price */}
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: tk.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: 2,
                    }}
                  >
                    {item.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: tk.textMuted,
                      fontFamily: '"JetBrains Mono", monospace',
                    }}
                  >
                    {formatEGP(item.price || 0)}/{item.unit_type === 'weight' ? 'KG' : 'pc'}
                  </div>
                </div>
                {/* Line total */}
                <div
                  style={{
                    textAlign: 'end',
                    fontSize: 13,
                    fontWeight: 700,
                    color: tk.text,
                    fontFamily: '"JetBrains Mono", monospace',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatEGP(item.total || 0)}
                </div>
                {/* Quantity controls — spans full width */}
                <div
                  style={{
                    gridColumn: '1 / -1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      background: tk.sunken,
                      border: `1px solid ${tk.border}`,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {item.unit_type !== 'weight' && (
                      <button
                        onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          width: 28,
                          height: 26,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'transparent',
                          border: 'none',
                          color: tk.textSecondary,
                          cursor: 'pointer',
                          flexShrink: 0,
                          transition: 'color 0.12s',
                        }}
                      >
                        <Minus size={12} />
                      </button>
                    )}
                    <CartQuantityInput
                      item={item}
                      tk={tk}
                      onUpdate={handleUpdateQuantity}
                      onAfterEdit={focusBarcodeInput}
                      inputRef={index === cart.length - 1 ? lastCartQuantityInputRef : undefined}
                    />
                    {item.unit_type !== 'weight' && (
                      <button
                        onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                          width: 28,
                          height: 26,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'transparent',
                          border: 'none',
                          color: tk.textSecondary,
                          cursor: 'pointer',
                          flexShrink: 0,
                          transition: 'color 0.12s',
                        }}
                      >
                        <Plus size={12} />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveFromCart(item.id)}
                    style={{
                      marginInlineStart: 'auto',
                      width: 28,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: tk.dangerSoft,
                      border: 'none',
                      borderRadius: 7,
                      color: tk.dangerText,
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'background 0.12s',
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Delivery fee row */}
          {activeOrder.mode === 'delivery' &&
            selectedDeliveryRegion &&
            selectedRegionFee > 0 && (
              <div
                style={{
                  padding: 10,
                  borderRadius: 8,
                  background: tk.accentSoft,
                  border: `1px solid ${tk.accent}33`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Package size={13} style={{ color: tk.accent }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: tk.text }}>
                      {t('pos.deliveryFee')}
                    </div>
                    <div style={{ fontSize: 10.5, color: tk.textMuted }}>{selectedDeliveryRegion}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: tk.text,
                      fontFamily: '"JetBrains Mono", monospace',
                    }}
                  >
                    {formatEGP(selectedRegionFee)}
                  </span>
                  <button
                    onClick={() => dispatch(setDeliveryRegion(''))}
                    style={{
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: tk.dangerSoft,
                      border: 'none',
                      borderRadius: 4,
                      color: tk.dangerText,
                      cursor: 'pointer',
                    }}
                  >
                    <X size={10} />
                  </button>
                </div>
              </div>
            )}
        </div>

        {/* Totals section */}
        <div
          style={{
            background: tk.surfaceAlt,
            borderTop: `1px solid ${tk.border}`,
            padding: '14px 16px 16px',
            borderRadius: '0 0 12px 12px',
            flexShrink: 0,
          }}
        >
          {/* Payment method selector */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginBottom: 14 }}>
            {[
              { id: 'cash', label: t('pos.cash'), icon: DollarSign },
              { id: 'electronic', label: t('pos.electronic'), icon: CreditCard },
              { id: 'mixed', label: t('pos.mixed'), icon: DollarSign },
            ].map(({ id, label, icon: Icon }) => {
              const isPayMethodLocked =
                (isZeroTotalCheckout && id !== 'cash') ||
                (id === 'mixed' && !isAnyMixedPaymentAvailable);
              const isPayActive =
                id === 'cash'
                  ? paymentMethod === 'cash'
                  : id === 'electronic'
                  ? ['card', 'instapay', 'wallet'].includes(paymentMethod) &&
                    paymentMethod !== 'mixed'
                  : paymentMethod === 'mixed';
              return (
                <button
                  key={id}
                  type="button"
                  disabled={isPayMethodLocked}
                  onClick={() => {
                    if (isPayMethodLocked) return;
                    if (id === 'cash') dispatch(setPaymentMethod('cash'));
                    else if (id === 'electronic') dispatch(setPaymentMethod(electronicType));
                    else dispatch(setPaymentMethod('mixed'));
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    height: 38,
                    borderRadius: 9,
                    border: `1px solid ${isPayActive ? tk.accent : tk.border}`,
                    background: isPayActive ? tk.accent : 'transparent',
                    color: isPayActive ? '#fff' : tk.textSecondary,
                    fontSize: 11.5,
                    fontWeight: isPayActive ? 600 : 400,
                    cursor: isPayMethodLocked ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    transition: 'all 0.15s',
                    boxShadow: isPayActive ? `0 2px 8px ${tk.accent}40` : 'none',
                    opacity: isPayMethodLocked ? 0.45 : 1,
                  }}
                >
                  <Icon size={12} />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Discount */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
              padding: '8px 10px',
              borderRadius: 8,
              background: tk.sunken,
            }}
          >
            <span style={{ fontSize: 12, color: tk.textSecondary, flex: 1 }}>
              {t('pos.discount')} (%)
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={localDiscountInput}
              onChange={handleDiscountChange}
              onBlur={handleDiscountBlur}
              placeholder="0"
              style={{
                width: 60,
                textAlign: 'center',
                background: tk.surface,
                border: `1px solid ${tk.border}`,
                borderRadius: 6,
                color: tk.text,
                fontSize: 12,
                padding: '4px 6px',
                fontFamily: '"JetBrains Mono", monospace',
                outline: 'none',
              }}
            />
          </div>

          {/* Totals rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11.5, color: tk.textSecondary }}>Subtotal</span>
              <span
                style={{
                  fontSize: 12,
                  color: tk.text,
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                {formatEGP(itemsTotal)}
              </span>
            </div>
            {promoPct > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11.5, color: tk.textSecondary }}>
                  {storePromo?.name ? `${storePromo.name} (${promoPct}%)` : `${t('pos.promo')} (${promoPct}%)`}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: tk.dangerText,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  -{formatEGP(promoAmount)}
                </span>
              </div>
            )}
            {discountPercentage > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11.5, color: tk.textSecondary }}>
                  Discount ({discountPercentage}%)
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: tk.dangerText,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  -{formatEGP(discountAmount)}
                </span>
              </div>
            )}
            {taxPct > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11.5, color: tk.textSecondary }}>Tax ({taxPct}%)</span>
                <span
                  style={{
                    fontSize: 12,
                    color: tk.text,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  +{formatEGP(itemsWithTax - itemsAfterDiscount)}
                </span>
              </div>
            )}
            {activeOrder.mode === 'delivery' && selectedRegionFee > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11.5, color: tk.textSecondary }}>Delivery fee</span>
                <span
                  style={{
                    fontSize: 12,
                    color: tk.text,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  +{formatEGP(selectedRegionFee)}
                </span>
              </div>
            )}
            {/* Divider */}
            <div style={{ height: 1, background: tk.border, margin: '2px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>
                {t('common.total')}
              </span>
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: tk.accent,
                  fontFamily: '"JetBrains Mono", monospace',
                  letterSpacing: '-0.5px',
                }}
              >
                {formatEGP(roundCurrency(cartTotal))}
              </span>
            </div>
          </div>

          {/* Checkout button */}
          <button
            onClick={handleCheckout}
            disabled={cart.length === 0 || checkoutLoading || loading}
            style={{
              width: '100%',
              height: 52,
              background:
                cart.length === 0 || checkoutLoading || loading
                  ? tk.sunken
                  : tk.accent,
              color:
                cart.length === 0 || checkoutLoading || loading
                  ? tk.textMuted
                  : '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: '0.01em',
              cursor:
                cart.length === 0 || checkoutLoading || loading
                  ? 'not-allowed'
                  : 'pointer',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'background 0.15s, transform 0.1s, box-shadow 0.15s',
              boxShadow:
                cart.length === 0 || checkoutLoading || loading
                  ? 'none'
                  : `0 4px 14px ${tk.accent}50`,
            }}
          >
            {checkoutLoading || loading ? (
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            ) : (
              t('pos.processPayment')
            )}
          </button>
        </div>
      </div>

      {/* ── Payment modal ───────────────────────────────────────────────────────── */}
      {showPaymentModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleProcessPayment();
            }}
            style={{
              background: tk.surface,
              border: `1px solid ${tk.border}`,
              borderRadius: 16,
              padding: 28,
              maxWidth: 440,
              width: '100%',
              margin: '0 16px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {/* Modal header */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: tk.text, marginBottom: 4 }}>
                {['card', 'instapay', 'wallet'].includes(paymentMethod)
                  ? t('pos.electronicPayment')
                  : paymentMethod === 'cash'
                  ? t('pos.cashPayment')
                  : t('pos.mixedPayment')}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: tk.textSecondary,
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                {t('pos.totalAmount')}: {formatEGP(roundCurrency(cartTotal))}
              </div>
            </div>

            {/* Cash payment */}
            {paymentMethod === 'cash' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>{t('pos.cashReceivedEgp')}</label>
                  <input
                    ref={cashInputRef}
                    type="text"
                    inputMode="decimal"
                    value={cashAmount}
                    onChange={(e) => handleAmountInput(e.target.value, setCashAmount)}
                    placeholder={t('pos.cashReceivedOptionalHint')}
                    style={{ ...inputStyle, height: 40, fontSize: 15, fontFamily: '"JetBrains Mono", monospace' }}
                  />
                </div>
                {cashAmount && parseFloat(cashAmount) >= cartTotal && (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      background: tk.successSoft,
                      border: `1px solid ${tk.success}33`,
                    }}
                  >
                    <span style={{ fontSize: 13, color: tk.successText, fontWeight: 600 }}>
                      {t('pos.change')}:{' '}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        color: tk.successText,
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 700,
                      }}
                    >
                      {formatEGP(roundCurrency(calculateChange()))}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Electronic payment */}
            {['card', 'instapay', 'wallet'].includes(paymentMethod) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Type selector */}
                <div>
                  <label style={labelStyle}>{t('pos.paymentType')}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {[
                      { id: 'card', label: t('pos.card'), icon: CreditCard },
                      { id: 'instapay', label: t('pos.instapay'), icon: Smartphone },
                      { id: 'wallet', label: t('pos.wallet'), icon: Wallet },
                    ].map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          setElectronicType(id);
                          dispatch(setPaymentMethod(id));
                        }}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: `1px solid ${paymentMethod === id ? tk.accent : tk.border}`,
                          background:
                            paymentMethod === id ? tk.accentSoft : 'transparent',
                          color: paymentMethod === id ? tk.accentText : tk.textSecondary,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          transition: 'all 0.12s',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                        }}
                      >
                        <Icon size={13} />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>{t('pos.transactionNumber')}</label>
                  <input
                    ref={electronicTxInputRef}
                    type="text"
                    maxLength={50}
                    value={
                      paymentMethod === 'card'
                        ? cardTransactionNo
                        : paymentMethod === 'instapay'
                        ? instapayTransactionNo
                        : walletTransactionNo
                    }
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '');
                      if (paymentMethod === 'card') setCardTransactionNo(digitsOnly);
                      else if (paymentMethod === 'instapay')
                        setInstapayTransactionNo(digitsOnly);
                      else setWalletTransactionNo(digitsOnly);
                    }}
                    placeholder={
                      paymentMethod === 'card'
                        ? t('pos.enterCardTransaction')
                        : paymentMethod === 'instapay'
                        ? t('pos.enterInstapayTransaction')
                        : t('pos.enterWalletTransaction')
                    }
                    style={{ ...inputStyle, height: 40 }}
                  />
                </div>
              </div>
            )}

            {/* Mixed payment */}
            {paymentMethod === 'mixed' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>{t('pos.cashAmountEgp')}</label>
                  <input
                    ref={cashInputRef}
                    type="text"
                    inputMode="decimal"
                    value={cashAmount}
                    onChange={(e) => {
                      let val = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = val.split('.');
                      if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
                      if (parts.length === 2 && parts[1].length > 2)
                        val = parts[0] + '.' + parts[1].slice(0, 2);
                      const numVal = parseFloat(val);
                      const maxCash = getMixedMaxCashAmount(mixedSecondaryMethod, cartTotal);
                      if (!isNaN(numVal) && numVal > maxCash) {
                        val = String(maxCash);
                      }
                      setCashAmount(val);
                    }}
                    placeholder="0"
                    style={{ ...inputStyle, height: 40, fontSize: 14, fontFamily: '"JetBrains Mono", monospace' }}
                  />
                  <div style={{ marginTop: 5, fontSize: 11, color: tk.textMuted }}>
                    {t('pos.mixedCashAmountHintBeforeZero')}
                    <span style={{ color: tk.accent, fontWeight: 600 }}>{MIXED_MIN_CASH_AMOUNT}</span>
                    {t('pos.mixedCashAmountHintBetween')}
                    <span style={{ color: tk.accent, fontWeight: 600 }}>
                      {formatEGP(mixedMaxCashAllowed)}
                    </span>
                  </div>
                </div>

                {/* Secondary method */}
                <div>
                  <label style={labelStyle}>{t('pos.secondaryPaymentMethod')}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {[
                      { id: 'card', label: t('pos.card'), icon: CreditCard },
                      { id: 'instapay', label: t('pos.instapay'), icon: Smartphone },
                      { id: 'wallet', label: t('pos.wallet'), icon: Wallet },
                    ].map(({ id, label, icon: Icon }) => {
                      const secondaryLocked = !isMixedAvailableForMethod(id);
                      return (
                      <button
                        key={id}
                        type="button"
                        disabled={secondaryLocked}
                        onClick={() => {
                          if (secondaryLocked) return;
                          setMixedSecondaryMethod(id);
                          setCardTransactionNo('');
                          setInstapayTransactionNo('');
                          setWalletTransactionNo('');
                          const maxCash = getMixedMaxCashAmount(id, cartTotal);
                          const currentCash = parseFloat(cashAmount) || 0;
                          if (currentCash > maxCash) {
                            setCashAmount(String(maxCash));
                          }
                        }}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: `1px solid ${mixedSecondaryMethod === id ? tk.accent : tk.border}`,
                          background: mixedSecondaryMethod === id ? tk.accentSoft : 'transparent',
                          color: mixedSecondaryMethod === id ? tk.accentText : tk.textSecondary,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: secondaryLocked ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit',
                          transition: 'all 0.12s',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          opacity: secondaryLocked ? 0.45 : 1,
                        }}
                      >
                        <Icon size={13} />
                        <span>{label}</span>
                      </button>
                    );
                    })}
                  </div>
                </div>

                {/* Secondary amount (auto) + transaction number */}
                {mixedSecondaryMethod === 'card' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>{t('pos.cardAmountAuto')}</label>
                      <input
                        type="number"
                        value={getMixedSecondaryAmount().toFixed(2)}
                        readOnly
                        style={{ ...inputStyle, height: 36, background: tk.sunken, fontFamily: '"JetBrains Mono", monospace' }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{t('pos.cardTransactionNumber')}</label>
                      <input
                        type="text"
                        maxLength={50}
                        value={cardTransactionNo}
                        onChange={(e) =>
                          setCardTransactionNo(e.target.value.replace(/\D/g, ''))
                        }
                        placeholder={t('pos.enterCardTransaction')}
                        style={{ ...inputStyle, height: 36 }}
                      />
                    </div>
                  </div>
                )}

                {mixedSecondaryMethod === 'instapay' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>{t('pos.instapayAmountAuto')}</label>
                      <input
                        type="number"
                        value={getMixedSecondaryAmount().toFixed(2)}
                        readOnly
                        style={{ ...inputStyle, height: 36, background: tk.sunken, fontFamily: '"JetBrains Mono", monospace' }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{t('pos.instapayTransactionNumber')}</label>
                      <input
                        type="text"
                        maxLength={50}
                        value={instapayTransactionNo}
                        onChange={(e) =>
                          setInstapayTransactionNo(e.target.value.replace(/\D/g, ''))
                        }
                        placeholder={t('pos.enterInstapayTransaction')}
                        style={{ ...inputStyle, height: 36 }}
                      />
                    </div>
                  </div>
                )}

                {mixedSecondaryMethod === 'wallet' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>{t('pos.walletAmountAuto')}</label>
                      <input
                        type="number"
                        value={getMixedSecondaryAmount().toFixed(2)}
                        readOnly
                        style={{ ...inputStyle, height: 36, background: tk.sunken, fontFamily: '"JetBrains Mono", monospace' }}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{t('pos.walletTransactionNumber')}</label>
                      <input
                        type="text"
                        maxLength={50}
                        value={walletTransactionNo}
                        onChange={(e) =>
                          setWalletTransactionNo(e.target.value.replace(/\D/g, ''))
                        }
                        placeholder={t('pos.enterWalletTransaction')}
                        style={{ ...inputStyle, height: 36 }}
                      />
                    </div>
                  </div>
                )}

                {cashAmount && parseFloat(cashAmount) > 0 && (
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      background: tk.accentSoft,
                      border: `1px solid ${tk.accent}33`,
                      fontSize: 12,
                      color: tk.accentText,
                      lineHeight: 1.8,
                      fontFamily: '"JetBrains Mono", monospace',
                    }}
                  >
                    <div>
                      <strong>{t('pos.cash')}:</strong> {formatEGP(cashAmount || 0)}
                    </div>
                    <div>
                      <strong>
                        {mixedSecondaryMethod === 'card'
                          ? t('pos.card')
                          : mixedSecondaryMethod === 'instapay'
                          ? t('pos.instapay')
                          : t('pos.wallet')}
                        :
                      </strong>{' '}
                      {formatEGP(getMixedSecondaryAmount())}
                    </div>
                    <div>
                      <strong>{t('common.total')}:</strong>{' '}
                      {formatEGP(roundCurrency(cartTotal))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div
              style={{
                display: 'flex',
                gap: 10,
                marginTop: 24,
              }}
            >
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                style={{
                  flex: 1,
                  height: 44,
                  background: 'transparent',
                  border: `1px solid ${tk.border}`,
                  borderRadius: 10,
                  color: tk.textSecondary,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={
                  cashPaymentInvalid ||
                  (paymentMethod === 'card' && requiresRefNum && !cardTransactionNo.trim()) ||
                  (paymentMethod === 'instapay' && requiresRefNum && !instapayTransactionNo.trim()) ||
                  (paymentMethod === 'wallet' && requiresRefNum && !walletTransactionNo.trim()) ||
                  mixedPaymentInvalid
                }
                style={{
                  flex: 1,
                  height: 44,
                  background: tk.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  opacity:
                    cashPaymentInvalid ||
                    (paymentMethod === 'card' && requiresRefNum && !cardTransactionNo.trim()) ||
                    (paymentMethod === 'instapay' && requiresRefNum && !instapayTransactionNo.trim()) ||
                    (paymentMethod === 'wallet' && requiresRefNum && !walletTransactionNo.trim()) ||
                    mixedPaymentInvalid
                      ? 0.45
                      : 1,
                }}
              >
                {t('pos.processPayment')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Receipt ──────────────────────────────────────────────────────────────── */}
      {(previewReceipt || (showReceipt && lastTransaction)) && (
        <Receipt
          transaction={previewReceipt || lastTransaction}
          storeName={storeName}
          receiptFooter={receiptFooter}
          website={website}
          phoneNumbers={phoneNumbers}
          socialMediaUrls={socialMediaUrls}
          logoPath={logoPath}
          logoSize={logoSize}
          includeDeliveryFee={includeDeliveryFee}
          onClose={() => {
            setPreviewReceipt(null);
            dispatch(closeReceipt());
          }}
        />
      )}

      {/* ── Discount approval modal (after payment, before save) ─────────────────── */}
      {showDiscountApprovalModal && pendingTransaction && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 60,
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmDiscountApproval();
            }}
            style={{
              background: tk.surface,
              border: `1px solid ${tk.border}`,
              borderRadius: 16,
              padding: 28,
              maxWidth: 440,
              width: '100%',
              margin: '0 16px',
            }}
          >
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: tk.text, marginBottom: 4 }}>
                {t('pos.discountApprovalTitle')}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'baseline',
                  gap: '0.35em',
                  fontSize: 13,
                  color: tk.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                <span>{t('pos.discount')}</span>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: tk.accent,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  {pendingTransaction.discount_percentage || 0}%
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    color: tk.dangerText,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  ({formatEGP(pendingTransaction.discount_amount || 0)})
                </span>
                <span style={{ color: tk.textMuted }}>—</span>
                <span>{t('common.total')}</span>
                <span
                  style={{
                    fontWeight: 600,
                    color: tk.text,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  {formatEGP(pendingTransaction.total_amount)}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={labelStyle}>
                  {t('pos.discountNotes')}{' '}
                  <span style={{ color: tk.textMuted, fontWeight: 400 }}>({t('common.optional')})</span>
                </label>
                <input
                  type="text"
                  value={discountNotes}
                  onChange={(e) => {
                    setDiscountNotes(e.target.value);
                    setDiscountApprovalError(null);
                  }}
                  placeholder={t('pos.discountNotesPlaceholder')}
                  maxLength={50}
                  style={inputStyle}
                />
              </div>
              {discountRequiresApproval && (
                <div {...discountApproverLatinKeyboardProps} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={labelStyle}>
                    {t('pos.discountApprovedBy')} ({t('settings.manager')}/{t('settings.admin')}{' '}
                    {t('pos.discountCode')})
                  </label>
                  <input
                    type="password"
                    name="discountApproverCode"
                    value={discountApproverCode}
                    onChange={(e) => {
                      setDiscountApproverCode(normalizeRefundCodeInput(e.target.value));
                      setDiscountApprovalError(null);
                    }}
                    maxLength={16}
                    placeholder={t('pos.discountCodePlaceholder')}
                    dir="ltr"
                    lang="en"
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoFocus
                    style={{ ...inputStyle, textAlign: isRTL ? 'right' : 'left' }}
                  />
                </div>
              )}
            </div>

            {discountApprovalError && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 12,
                  marginBottom: 16,
                  borderRadius: 8,
                  fontSize: 13,
                  background: tk.dangerSoft,
                  color: tk.dangerText,
                  border: `1px solid ${tk.danger}`,
                }}
              >
                <AlertCircle size={18} style={{ flexShrink: 0 }} />
                {discountApprovalError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={handleCancelDiscountApproval}
                disabled={checkoutLoading}
                style={{
                  flex: 1,
                  height: 44,
                  background: 'transparent',
                  border: `1px solid ${tk.border}`,
                  borderRadius: 10,
                  color: tk.textSecondary,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: checkoutLoading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: checkoutLoading ? 0.6 : 1,
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={checkoutLoading || !discountSaleCanSubmit}
                style={{
                  flex: 1,
                  height: 44,
                  background: tk.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: checkoutLoading ? 1 : !discountSaleCanSubmit ? 0.45 : 1,
                }}
              >
                {checkoutLoading ? (
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      animation: 'spin 0.8s linear infinite',
                    }}
                  />
                ) : (
                  t('pos.completeSale')
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Close session modal ───────────────────────────────────────────────────── */}
      {showCloseSessionModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: tk.surface,
              border: `1px solid ${tk.border}`,
              borderRadius: 16,
              padding: 28,
              maxWidth: 400,
              width: '100%',
              margin: '0 16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 700, color: tk.text }}>
                {t('sessions.closeSession')}
              </div>
              <button
                onClick={() => setShowCloseSessionModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: tk.textMuted,
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 6,
                }}
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 24 }}>
              {t('alerts.closeSession')}
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowCloseSessionModal(false)}
                style={{
                  flex: 1,
                  height: 44,
                  background: 'transparent',
                  border: `1px solid ${tk.border}`,
                  borderRadius: 10,
                  color: tk.textSecondary,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCloseSession}
                disabled={sessionLoading}
                style={{
                  flex: 1,
                  height: 44,
                  background: sessionLoading ? tk.dangerSoft : tk.danger,
                  color: sessionLoading ? tk.dangerText : '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: sessionLoading ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {sessionLoading ? (
                  <>
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: `2px solid ${tk.dangerText}44`,
                        borderTopColor: tk.dangerText,
                        animation: 'spin 0.8s linear infinite',
                        flexShrink: 0,
                      }}
                    />
                    {t('sessions.closingSessionInProgress')}
                  </>
                ) : (
                  t('sessions.closeSession')
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .pos-checkout-btn:not(:disabled):active { transform: scale(0.98) !important; box-shadow: none !important; }
        .pos-product-card:not([data-out-of-stock]):active { transform: scale(0.96) !important; box-shadow: none !important; }
        .pos-cat-row::-webkit-scrollbar { display: none; }
.pos-order-tabs-row::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default POS;
